/* eslint-disable camelcase */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Common Application utilities
 *
 * @file CommonUtils.js
 * @author Prithviraj K
 */
import React from 'react';
import {
  matchPath
} from 'react-router-dom';
import Framework from '@greenville/framework';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { getSnapshot } from 'mobx-state-tree';
import { EventProvider } from '@aquila/core';
import {
  deviceType, isChrome, isFirefox, isSafari, isOpera, isIE, isEdge, osName,
  fullBrowserVersion, osVersion
} from 'react-device-detect';
import { publicIpv4 } from 'public-ip';
import { dateTimeUtils, statusColorUtils } from '@aet/components';
import env from '../env';
import PathUtils from './PathUtils';
import LmsLaunchContext from './LmsLaunchContext';
import * as constants from '../constants';
import * as GAConstants from '../GAConstants';
import {
  initESW as Chatbot,
  handleChatBotIframeEvent,
  registerEmbeddedMessagingOpenChatFromQueryParam
} from './ChatbotScript';
import packageInfo from '../../../package.json';
import UserPreferencesService from '../../modules/authhome/service/UserPreferencesService';
import UserBookPreferencesService from '../../modules/authhome/service/UserBookPreferencesService';
import PDPRedirectService from '../../modules/authhome/service/PDPRedirectService';
import ProductUtils from './ProductUtils';
import { AET_STORE_EVENTS } from '../../modules/assignableEtext/common/Constants';

const userPreferencesService = new UserPreferencesService();
const userBookPreferencesService = new UserBookPreferencesService();
const pdpRedirectService = new PDPRedirectService();
export default class CommonUtils {
  static templateData = {};

  static loadUuid = this.getUUID();

  static loadDt = new Date().toISOString();

  static userLocalLoadDt = CommonUtils.getLocalIsoTime();

  static loadTime = new Date().getTime();

  static observer = null;

  static dashboardObserver = null;

  static elementIntersectObserver = null;

  static userEntitlementMixLabel = null;

  static isHEIUserStatus = false;
  
  static controller = null;
  
  static cachedTiggeredEvents = {};


  static viewEventTracker = new Map();

  static pendingHeroInsightsRefetch = false;

  static setPendingHeroInsightsRefetch = (value) => {
    CommonUtils.pendingHeroInsightsRefetch = Boolean(value);
  };

  static isPendingHeroInsightsRefetch = () => CommonUtils.pendingHeroInsightsRefetch;

  static clearPendingHeroInsightsRefetch = () => {
    CommonUtils.pendingHeroInsightsRefetch = false;
  };

  static isHeroAetCourse = (courseId) => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner?.getHeroBook?.() || {};

    return Boolean(courseId && courseId === heroBook.course_id && herobanner.isAETEnabled);
  };

  /**
   * Get user_entitlement_mix
   *
   * @param {*} books
   * @returns
   */

  static getUserEntitlementMix = (books) => {
    if (this.userEntitlementMixLabel) {
      return this.userEntitlementMixLabel;
    }
    const {
      DIRECT_TO_CONSUMER,
      DIRECT_TO_CONSUMER_IA,
      DIRECT_TO_CONSUMER_HEI,
      COURSE_WARE,
      CG_RENEWALS,
      DIRECT_TO_LEARNER,
      DIRECT_TO_LEARNER_RENEWAL,
      INTEGRATED_SMS,
      INTEGRATED_RUMBA,
      DIGITAL_SAMPLE_MARKETING,
      DIGITAL_SAMPLE_SELF,
      DIGITAL_SAMPLE_REP,
      DIRECT_TO_CONSUMER_PERPETUAL,
      DIRECT_TO_CONSUMER_HEIUK,
      DIRECT_TO_CONSUMER_HEICA,
      DIRECT_TO_CONSUMER_HEILATAM,
      INSTRUCTOR_LED_SAET,
      INSTRUCTOR_LED_SAETIA
    } = constants;
    const userEntitlementMixLabel = [];

    const bmcMapping = {
      [DIRECT_TO_CONSUMER]: 1,
      [DIRECT_TO_CONSUMER_IA]: 2,
      [DIRECT_TO_CONSUMER_HEI]: 3,
      [COURSE_WARE]: 4,
      [CG_RENEWALS]: 5,
      [DIRECT_TO_LEARNER]: 6,
      [DIRECT_TO_LEARNER_RENEWAL]: 7,
      [INTEGRATED_SMS]: 8,
      [INTEGRATED_RUMBA]: 9,
      [DIGITAL_SAMPLE_MARKETING]: 10,
      [DIGITAL_SAMPLE_SELF]: 11,
      [DIGITAL_SAMPLE_REP]: 12,
      [constants.USER_ENTITLEMENT_MIX_LABEL.OTHER]: 13,
      [DIRECT_TO_CONSUMER_PERPETUAL]: 14,
      [DIRECT_TO_CONSUMER_HEIUK]: 15,
      [DIRECT_TO_CONSUMER_HEICA]: 16,
      [DIRECT_TO_CONSUMER_HEILATAM]: 17,
      [INSTRUCTOR_LED_SAET]: 18,
      [INSTRUCTOR_LED_SAETIA]: 19
    };

    const activeBooks = this.getActiveBooks(books);

    activeBooks.forEach((book) => {
      const businessModeCode = book.product_entitlements?.business_model_code;
      if (businessModeCode) {
        const bmcMappingNumber = bmcMapping[businessModeCode] || bmcMapping[constants.USER_ENTITLEMENT_MIX_LABEL.OTHER];
        if (bmcMappingNumber && !userEntitlementMixLabel.includes(bmcMappingNumber)) {
          userEntitlementMixLabel.push(bmcMappingNumber);
        }
      }
    });

    this.userEntitlementMixLabel = userEntitlementMixLabel.join(',') || null;

    return this.userEntitlementMixLabel;
  };

  /**
   * Function to Initiate Google Analytics
   */
  static initializeGTM() {
    if (!window.google_tag_manager) {
      Framework.getEventManager().publish(constants.GTM_REQUESTED);
    }
  }

  /**
   * Function to lazyload and preload component
   *
   * @param {Module} component
   */
  static lazyWithPreload(component) {
    const lazyComponent = React.lazy(component);
    lazyComponent.preload = component;
    return lazyComponent;
  }

  static tocList(toc, pageId) {
    let found;
    const findAsset = (tocData) => {
      tocData.forEach((child) => {
        if (child.id === pageId) {
          found = child;
        }
        if (child.children) { findAsset(child.children); }
      });
      return found;
    };
    findAsset(toc);
    return found;
  }

  static getBrowserName() {
    let browserName = '';
    switch (true) {
      case isChrome:
        browserName = 'Chrome';
        break;
      case isFirefox:
        browserName = 'Firefox';
        break;
      case isSafari:
        browserName = 'Safari';
        break;
      case isOpera:
        browserName = 'Opera';
        break;
      case isIE:
        browserName = 'Internet Explorer';
        break;
      case isEdge:
        browserName = 'Edge';
        break;
      default:
        browserName = navigator.appName;
        break;
    }
    return browserName;
  }

  static getDeviceType = () => {
    if (deviceType === 'browser') return 'PC';
    return deviceType;
  };

  static pushGlobalParams() {
    const globalParams = {
      app_id: env.PRODUCT && env.PRODUCT.toUpperCase(),
      app_version: packageInfo.version,
      user_timezone_offset: Math.abs(new Date().getTimezoneOffset() / 60),
      web_browser: this.getBrowserName(), // Browvser Name
      device_type: this.getDeviceType(), // Device Type, Ex: PC, Tablet, Phone
      device_id: (window.piSession && window.piSession.getDeviceId()) || null,
      operating_system_code: osName,
      environment_code: env.ENVIRONMENT.toUpperCase(),
      ga_client_id: env.CLIENT_ID
    };

    return EventProvider.getEventManager().dispatch(globalParams);
  }

  static initTelemetryPageAction() {
    return EventProvider.getEventManager().dispatch({
      event: constants.TELEMTRY_INIT
    });
  }

  static logUserTelemetryEvent(course, userId) {
    const userParams = {
      person_id: userId,
      person_id_type: constants.ID_TYPE,
      person_role_code: course.role || 'Student',
      login_session_id: window.piSession && window.piSession.getContextId(), // PI Session Id of the logged user
      secondary_person_id: null,
      secondary_person_id_type: null,
      secondary_person_role_code: null,
      organization_id: course.organizationId || null,
      organization_id_type: constants.ORGANIZATION_ID_TYPE
    };
    return EventProvider.getEventManager().dispatch(userParams);
  }

  static logTelemetryPageAction(event) {
    const {
      category,
      action,
      label,
      value
    } = event;
    return EventProvider.getEventManager().dispatch({
      event: constants.TELEMETRY_PAGE_ACTION,
      eventCategory: category || 'NA',
      eventAction: action || 'NA',
      eventLabel: label || 'NA',
      eventValue: value || 'NA',
      transactionDt: new Date().toISOString()
    });
  }

  static logTelemetryPageError(error) {
    const {
      errorCode,
      errorUrl,
      errorMessage,
      errorStateCode,
      errorDetail
    } = error;
    return EventProvider.getEventManager().dispatch({
      event: constants.TELEMETRY_PAGE_ERROR,
      errorCode,
      errorUrl,
      errorMessage,
      errorStateCode,
      errorDetail,
      transactionDt: new Date().toISOString()
    });
  }

  static getUUID() {
    const crypto = window.crypto || window.msCrypto;
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      // eslint-disable-next-line
      (crypto && (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)));
  }

  static getLocalIsoTime() {
    const tzoffset = (new Date()).getTimezoneOffset() * 60000; // offset in milliseconds
    const localISOTime = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);
    return localISOTime;
  }

  /**
   * Function to load the PLA events
   * @param {object} model - Model
   * @param {string} pageId - pageId
   * @param {Boolean} isFromCourse - Is from course
   * @param {string} previousPageId - pageId
   */

  static sendCommonPLAEvents(model, pageId, isFromCourse, previousPageId, loadType, navData) {
    const product = isFromCourse ? model.getProduct() : model;
    const currentTime = new Date().getTime();
    const durationSeconds = ((currentTime - this.loadTime) / 1000).toFixed(0);
    const unloadUuId = this.loadUuid;
    const updatedLoadId = this.getUUID();
    const unloadDt = this.loadDt;
    const updatedLoadDt = new Date().toISOString();
    const unloadUserLocalLoadDt = this.userLocalLoadDt;
    const updatedUserLocalLoadDt = this.getLocalIsoTime();
    product.setPLAEvents(model, pageId, isFromCourse, previousPageId, durationSeconds, updatedLoadId, updatedLoadDt,
      updatedUserLocalLoadDt, unloadUuId, unloadDt, unloadUserLocalLoadDt, loadType, navData);
    this.loadTime = new Date().getTime();
    this.loadUuid = updatedLoadId;
    this.loadDt = updatedLoadDt;
    this.userLocalLoadDt = updatedUserLocalLoadDt;
  }

  /**
   * Function to set audio  url
   * @param {array} audioUri - File Name
   * @param {string} metaDataVersionId - Version ID
   * @param {string} metaDataAssetTypeUri - Asset Type URL
   * @param {string} metaBasePath - Base Path
   */

  static setUrl(audioUri, metaDataVersionId, metaDataAssetTypeUri, metaBasePath) {
    // TODO: Need to fix it from service end by adding flag for human readable audio
    const audioUrl = audioUri.map(val => (val.startsWith('http') ? `${val}` : `${metaBasePath}${metaDataVersionId}${metaDataAssetTypeUri}${val}`));
    return audioUrl;
  }

  /**
   * Function to set template data
   *
   * @param {object} data - Template data
   */
  static setTemplateData(data) {
    this.templateData = data;
  }

  /**
   * Function to get template data
   *
   * @param {string} containerName - Container name
   * @param {string} propertyName - Property name
   * @param {any} defaultValue - Default value
   * @returns {any} Template data
   */
  static getTemplateData(containerName, propertyName, defaultValue = null) {
    return this.getContainerData(this.templateData, containerName, propertyName, defaultValue);
  }

  /**
   * Function to get exam mode configuration based on exam mode code
   *
   * @param {string} examModeCode - Exam mode code
   * @returns {object} Exam mode configuration
   */
  static getExamModeConfiguration(examModeCode) {
    const examModeCodes = this.getTemplateData('restrict', 'code', []);

    if (!Array.isArray(examModeCodes)) {
      return {};
    }

    return examModeCodes.find(restrictCode => restrictCode?.name === examModeCode)?.config || {};
  }

  /**
   * Function to set pdf audio url
   * @param {array} audioUri - File Name
   * @param {object} asset - Asset values
   */
  static setPdfAudioUrl(audioUri, asset) {
    const metaBasePath = asset.audioMetadata.basePath;
    const metaDataVersionId = asset.audioMetadata.assetTypes[0].versionId;
    const metaDataAssetTypeUri = asset.audioMetadata.assetTypes[0].uri;
    const pdfAudioUrl = this.setUrl(audioUri, metaDataVersionId, metaDataAssetTypeUri, metaBasePath);

    return pdfAudioUrl;
  }

  /* Function to decode string to object
   * @param {string} value
   */

  static deCodeBase64(value) {
    // eslint-disable-next-line no-undef
    const base64Str = atob(value);
    const base64 = JSON.parse(base64Str);
    return base64;
  }

  /**
   * Function to encode from object to string
   * @param {object} obj
   */

  static enCodeBase64(obj) {
    const base64 = JSON.stringify(obj);
    // eslint-disable-next-line no-undef
    const base64Str = btoa(base64);
    return base64Str;
  }

  /**
   * Function to set cookie
   * @param {string} cookiename
   * @param {string} value
   * @param {integer} maxAge
   */

  static setCookie(name, value, maxAge) {
    const date = new Date();
    date.setTime(date.getTime() + maxAge);
    const domain = '.pearson.com';
    document.cookie = `${name}=${value};path=/;domain=${domain};expires=${date.toGMTString()}`;
  }

  /**
   * Function to get cookie token
   * @param {string} cname
   */
  static getCookie(cname) {
    const name = `${cname}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  /**
   * Function to trigger GTM
   * when user clicks on disconnect
   * @param {string} deviceId
   */
  static deviceDisconnectEvent(deviceId) {
    const deviceDisconnectEvent = {
      category: constants.DISCONNECT_DEVICE,
      event_category: 'Device Management',
      event_action: 'Disconnect Device',
      event_label: 'deviceId',
      event_value: deviceId,
      event: constants.DISCONNECT_DEVICE,
      disconnected_device_id: deviceId,
      transaction_local_dt: this.getLocalIsoTime(),
      user_local_event_dt: this.getLocalIsoTime(),
      message_id: this.getUUID(),
      event_unix_timestamp: new Date().getTime()
    };

    publicIpv4().then(
      (ipAddress) => {
        deviceDisconnectEvent.disconnected_device_ip = ipAddress;
        EventProvider.getEventManager().dispatch(deviceDisconnectEvent);
      }
    );
  }

  /**
    * Function to trigger device mangement otp and link events
    * when otp and link is clicking
    *
    */
  static deviceManagementOTPEvents(eventName, category, action, status) {
    const otpEvent = {
      event: eventName,
      event_category: category,
      event_label: status,
      transaction_local_dt: CommonUtils.getLocalIsoTime(),
      user_local_event_dt: CommonUtils.getLocalIsoTime(),
      message_id: CommonUtils.getUUID(),
      event_unix_timestamp: new Date().getTime(),
      event_action: action
    };
    EventProvider.getEventManager().dispatch(otpEvent);
  }

  /**
    * Function to trigger device mangement send and resend otp and link events
    * when otp and link is clicking
    *
    */
  static deviceManagementReSentOTPEvents(eventName) {
    const otpEvent = {
      event: eventName,
      transaction_dt: new Date().toISOString(),
      transaction_local_dt: CommonUtils.getLocalIsoTime(),
      message_id: CommonUtils.getUUID()
    };
    EventProvider.getEventManager().dispatch(otpEvent);
  }

  /**
   * Function to trigger Account setting
   */
  static accountSettingEvent() {
    const deviceDisconnectEvent = {
      event: constants.ACCOUNT_SETTING,
      event_action: '',
      transaction_local_dt: this.getLocalIsoTime(),
      message_id: this.getUUID()
    };
    EventProvider.getEventManager().dispatch(deviceDisconnectEvent);
  }

  /**
   * Function to trigger page view for bookshelf
   */
  static sendPageViewEvent() {
    const navigationEvent = {
      event: constants.PAGE_VIEW,
      page_name: constants.MOJO_HOME,
      content_id_type: env.PRODUCT && env.PRODUCT.toUpperCase(),
      message_id: this.getUUID(),
      transaction_dt: new Date().toISOString(), // Timestamp in ISO Format: 2020-04-15T12:33:45.237Z
      transaction_local_dt: CommonUtils.getLocalIsoTime() // "2021-05-04T17:38:48:195"
    };
    EventProvider.getEventManager().dispatch(navigationEvent);
  }

  /**
   * Function to get cookie expire time
   */

  static getExpireTime() {
    const currentTime = new Date();
    const currentGetTime = currentTime.getTime();
    const expireTime = currentGetTime + 1000 * 36000;
    currentTime.setTime(expireTime);
    return currentTime.toUTCString();
  }

  /**
   * Function to push the launch study telemetry event
   */
  static launchStudyPush = () => {
    const launchStudy = {
      event: constants.LAUNCH_STUDY_EVENT,
      event_action: constants.LAUNCH_STUDY_EVENT_ACTION,
      event_category: constants.DRAWER_PANEL_STUDY,
      event_label: constants.LAUNCH_STUDY_EVENT_LABEL,
      event_unix_timestamp: new Date().getTime(),
      event_value: constants.DRAWER_PANEL_STUDY,
      launch_method: constants.BOOKSHELF,
      transaction_local_dt: this.getLocalIsoTime(),
      user_local_event_dt: this.getLocalIsoTime()
    };
    EventProvider.getEventManager().dispatch(launchStudy);
  };

  /**
   * Function to get page name
   * @param {string} pageId
   */
  static getPageName(pageId) {
    const assetInfo = Framework.getStoreRegistry().getStore('asset');
    const slatesInfo = assetInfo && assetInfo.slates && assetInfo.slates.length > 0 && CommonUtils.tocList(assetInfo.slates, pageId);
    const childrenInfo = assetInfo && assetInfo.children && assetInfo.children.length > 0 && CommonUtils.tocList(assetInfo.children, pageId);
    const pageName = (slatesInfo && slatesInfo.title) || (childrenInfo && childrenInfo.title) || this.getAppTitle();
    return pageName;
  }


  /**
    * get Theme based on Domain
    */
  static getAppTheme() {
    this.theme = constants.STANDARD_THEME;
    if (window.location.hostname.includes(constants.PLUS)) {
      this.theme = constants.PLUS;
    }
    return this.theme;
  }

  static getAppTitle() {
    this.title = constants.TITLE;
    if (window.location.hostname.includes(constants.PLUS)) {
      this.title = constants.PEARSON_PLUS_TITLE;
    }

    return this.title;
  }

  /**
   * Configuration for AET route patterns
   * Each key maps to a function that builds the route
   */
  static AET_ROUTE_BUILDERS = {
    HOME: base => base,
    ASSIGNMENTS: base => `${base}/assignments`,
    ASSIGNMENT_DETAILS: (base, { assignmentId }) => `${base}/assignments/${assignmentId}`,
    STUDENT_ROSTER: base => `${base}/student-roster`,
    STUDENT_ROSTER_DETAILS: (base, { studentId }) => `${base}/student-roster/${studentId}`,
    STUDENT_REVIEW: base => `${base}/review`,
    STUDENT_DETAILS: (base, { assignmentId, studentId }) => `${base}/assignments/${assignmentId}/students/${studentId}`,
    DIFFICULT_TOPIC_DETAILS: (base, { assignmentId, difficultTopicId }) => `${base}/assignments/${assignmentId}/difficult-topic/${difficultTopicId}`
  };

  static INSTRUCTOR_COURSE_ROUTE_BUILDERS = {
    ALL: base => `${base}/course`,
    PAST: base => `${base}/course/past`,
    ARCHIVED: base => `${base}/course/archived`,
    NEW: base => `${base}/course/new`,
    CREATE: base => `${base}/course/create`,
    COPY: (base, { courseId }) => `${base}/course/${courseId}/copy`,
    SETTINGS: (base, { courseId }) => `${base}/course/${courseId}/settings`,
    ASSIGNMENT_SETTINGS: (base, { courseId }) => `${base}/course/${courseId}/assignment-settings`,
    ROSTER: (base, { courseId }) => `${base}/course/${courseId}/roster`
  };

  /**
   * Gets the base context from current route
   * @param {object} match - React Router match object
   * @returns {'/home' | '/home2' | '/aet/home' | '/home2/:mlmcontextencoded'} Base context path
   */
  static getBaseContext = (match) => {
    const path = match?.path || window.location.pathname;
    const mlmcontextencoded = match?.params?.mlmcontextencoded;

    // Check in order of specificity
    if (path.startsWith('/home2')) {
      // Only preserve mlmcontextencoded for valid MLM deeplink paths. Segments like
      // "course" or "assignments" also match /home2/:mlmcontextencoded but are not MLM routes.
      const mlmBookData = CommonUtils.getMLMRouteBookData(match) || {};
      const isValidMLMRoute = mlmBookData?.bookId || mlmBookData?.courseId;

      if (mlmcontextencoded && isValidMLMRoute) {
        return `/home2/${mlmcontextencoded}`;
      }

      return '/home2';
    }

    if (path.startsWith('/home') && !path.startsWith('/aet')) {
      return '/home';
    }

    return '/aet/home';
  };

  /**
   * Builds a context-aware route
   * @param {string} routeName - Name of the route (e.g., 'ASSIGNMENTS', 'ASSIGNMENT_DETAILS')
   * @param {object} match - React Router match object
   * @param {object} params - Route parameters (e.g., { assignmentId: '123' })
   * @returns {string} Full route path
   */
  static getContextAwareRoute = (routeName, match, params = {}) => {
    const baseContext = CommonUtils.getBaseContext(match);
    const routeBuilder = CommonUtils.AET_ROUTE_BUILDERS[routeName]
      || CommonUtils.INSTRUCTOR_COURSE_ROUTE_BUILDERS[routeName];

    if (!routeBuilder) {
      console.warn(`[getContextAwareRoute] Unknown route: ${routeName}`);
      return baseContext;
    }

    // Build the route - baseContext already includes mlmcontextencoded if it exists
    const route = routeBuilder(baseContext, params);

    // If baseContext includes mlmcontextencoded, ensure it's preserved in the built route
    // The routeBuilder should handle this, but we verify here
    return CommonUtils.redirectTo(route);
  };

  /**
   * Function to redirect
   * @param {string} businessModelCode
   * @param {boolean} tpiFlag
   */
  static redirectToUrl(businessModelCode, tpiFlag, sourceFrom = null, course = {}) {
    const {
      EREADER_UTM_SOURCE
    } = constants;
    if (sourceFrom) {
      const mlmHome2RedirectUrl = CommonUtils.getBase64Decoded(sourceFrom);

      if (mlmHome2RedirectUrl) {
        return mlmHome2RedirectUrl;
      }
    }

    const queryParamUrl = PathUtils.getQueryParameterbyName('redirectURL');
    // To identify source from normal home or home2 for MLM route
    // Redirect to home 2 incase of MLM deeplink or home2
    const isMLMdeepLink = this.isQueryParamAvailable(constants.LMS, 'y');
    const isLMSdeepLink = CommonUtils.isLMSCourse();
    const authHomeRoute = ((
      sourceFrom
      && typeof sourceFrom === 'string'
      && sourceFrom.toLowerCase() === constants.HOME2
    )
    || isMLMdeepLink || isLMSdeepLink
    ) ? 'home2' : 'home';
    // commented below for redirecting console page to authhome
    // || PathUtils.getQueryParameterbyName('returnUrl')
    // || PathUtils.getQueryParameterbyName('returnurl');
    let redirectUrl = `${env.CONTENT_HOST}/${authHomeRoute}?${EREADER_UTM_SOURCE}`;
    if (tpiFlag || (window.location.hostname.includes(constants.ETEXT) && !queryParamUrl)) {
      redirectUrl = `${env.CONTENT_HOST}/${authHomeRoute}?${EREADER_UTM_SOURCE}&isTpi=Y`;
    } else if (!sourceFrom && !isMLMdeepLink && queryParamUrl) {
      redirectUrl = PathUtils.addQueryParameter(EREADER_UTM_SOURCE, queryParamUrl);
    }
    // for authhome to ereader scenario, this is handled by existing logic
    // of adding sourcefrom to the url.
    // In case of direct ereader deeplinks, we handle this by below logic
    else if (isLMSdeepLink && course.id) {
      const searchParams = window.location.search
        ? (window.location.search.startsWith('?')
          ? window.location.search.replace('?', '&')
          : window.location.search)
        : '';
      redirectUrl = `${env.CONTENT_HOST}/${authHomeRoute}/${window.btoa(`courses/${course.id}`)}?${EREADER_UTM_SOURCE}${searchParams}`;
    }
    return redirectUrl;
  }

  static backToHome = (sourceFrom = null) => {
    const isMLMdeepLink = this.isQueryParamAvailable(constants.LMS, 'y');
    const isLMSdeepLink = CommonUtils.isLMSCourse();
    const authHomeRoute = ((
      sourceFrom
      && typeof sourceFrom === 'string'
      && sourceFrom.toLowerCase().includes(constants.HOME2.toLowerCase())
    )
    || isMLMdeepLink || isLMSdeepLink
    ) ? 'home2' : 'home';

    Framework.redirectTo(`/${authHomeRoute}`);
  };

  /**
   * Function to get glossary language from pdf locale
   * @param {string} language
   */
  static getGlossaryLanguage(language) {
    const frenchLocale = [constants.FR_FR, constants.FR_CA];
    let glossaryLanguage = '';

    if (language.indexOf('-') > -1) {
      if (frenchLocale.includes(language)) {
        glossaryLanguage = constants.FR_FR.replace('-', '_');
      } else {
        glossaryLanguage = language.replace('-', '_');
      }
    } else {
      switch (language) {
        case constants.JA:
          glossaryLanguage = constants.JA_JP;
          break;
        case constants.KO:
          glossaryLanguage = constants.KO_KR;
          break;
        default:
          glossaryLanguage = language.concat('_', language);
          break;
      }
    }

    return glossaryLanguage;
  }

  /**
   * Check session and logout if empty
  */
  static checkSession() {
    // TODO: Session needs to be checked properly since hasValidSession doesn't work as expected
    if (window.piSession.getContextId() === '') {
      window.location.reload();
    }
  }

  /**
   * Function to push telementry/GA event
   * @param {string} event_category
   * @param {string} event_name
   * @param {string} event_action
   * @param {string} event_label
   * @param {object} customFields or extraFields
   */
  static dispatchGaEvent(eventCategory, event, action, label, customFields = null) {
    let eventContent = {
      event_category: eventCategory,
      event_action: action,
      event_label: label,
      event,
      event_name: event,
      transaction_local_dt: this.getLocalIsoTime(),
      user_local_event_dt: this.getLocalIsoTime(),
      event_unix_timestamp: new Date().getTime(),
      message_id: this.getUUID()
    };
    if (customFields) {
      eventContent = { ...customFields, ...eventContent };
    }
    EventProvider.getEventManager().dispatch(eventContent);
  }

  /**
   * Function to push AB experiment exposure telemetry/GA event
   * @param {string} experiment_name
   * @param {string} experiment_variant_name
   * @param {object} customFields
   */
  static experimentDispatchEvent(experiment_name, experiment_variant_name, customFields = {}) {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const coursecollection = Framework.getStoreRegistry().getStore('coursecollection');
    const currentCourse = coursecollection?.getActiveCourse() || {};
    const heroBook = herobanner?.getHeroBook?.() || {};
    const eventContent = {
      app_id: constants.EXPERIMENT_APP_ID,
      channel_type: null,
      visitor_id: window.piSession.userId(),
      user_type: null,
      user_signed_in: 'true',
      channel_flow: null,
      channel_section: null,
      user_entitlement_mix: this.getUserEntitlementMix(),
      app_activity_area_code: constants.AUTH_HOME_WEB_CATEGORY,
      course_id: heroBook?.course_id || null,
      experiment_name,
      experiment_variant_name: this.toString(experiment_variant_name),
      mlm_subs: currentCourse?.isMLMcourse ? 'true' : 'false',
      channels_p_bundle_subs: null,
      channels_subs: herobanner.hasAccessToExactOrLiteMatch() ? 'true' : 'false',
      non_subs: heroBook.gpsSubscriptionOfBook?.status !== constants.ACTIVE ? 'true' : 'false',
      pplus_subs: heroBook.gpsSubscriptionOfBook?.status === constants.ACTIVE ? 'true' : 'false',
      channels_sub_status: this.getChannelSubscriptionType(),
      ...customFields
    };
    this.dispatchGaEvent(
      constants.EXPERIMENT_EVENT.EVENT_CATEGORY,
      constants.EVENT_USER_INTERACTION,
      constants.EXPERIMENT_EVENT.EVENT_ACTION,
      null,
      eventContent);
  }

  /**
   * Get AET custom fields
   *
   * @param {Object} context
   * @param {String} locationInApp
   * @returns {Object}
   */
  static aetCustomFields = (context, locationInApp, initiatePoint = null, overRideBook = null) => {
    const book = context?.heroDashboardPlugin?.getHeroBook?.() || overRideBook || {};

    return {
      person_role_code: CommonUtils.getIsStudent(book) ? 'student' : 'instructor',
      course_id: book.course_id || null,
      course_section_id: book.course_id || null,
      organization_id: book.organizationId || null,
      book_id: book.book_id || null,
      ...(locationInApp ? { location_in_app: locationInApp } : {}),
      ...(initiatePoint ? { initiate_point: initiatePoint } : {}),
      assignable_etext: book.isAETEnabled ? 'true' : 'false',
      assigned_etext: book.isAet ? 'true' : 'false'
    };
  };

  /**
   * Function to push Resend telementry/GA event
   * @param {string} event_category
   * @param {string} event_name
   * @param {string} event_action
   */
  static dispatchResendGaEvent(eventCategory, event, action) {
    const eventContent = {
      event_category: eventCategory,
      event_action: action,
      event,
      event_name: event,
      transaction_dt: new Date().toISOString(),
      transaction_local_dt: CommonUtils.getLocalIsoTime(),
      message_id: CommonUtils.getUUID()
    };
    EventProvider.getEventManager().dispatch(eventContent);
  }

  /**
   * Returns true if user has a subscription with the provided source
   *
   * @param {Object} userDetail
   * @param {string} subscriptionSource
   * @param {Array} statusList
   * @param {Array} entitlementLevelList
   */
  static hasSubscriptionWithSource(
    userDetail,
    subscriptionSource,
    statusList = [constants.ACTIVE, constants.PAUSED],
    entitlementLevelList = [constants.ENTITLEMENTLEVEL_SINGLE, constants.BUNDLE, constants.ENTITLEMENTLEVEL_MULTI]
  ) {
    return userDetail.gpsSubscriptions.some(
      subscription => subscription?.subscriptionSource === subscriptionSource
        && (subscription?.status && statusList.includes(subscription.status))
        && subscription?.entitlementLevel && entitlementLevelList.includes(subscription.entitlementLevel)
    );
  }

  /**
   * Returns true if user has a subscription
   *
   * @param {Object} userDetail
   * @param {Array} statusList
   * @param {Array} entitlementLevelList
   */
  static hasSubscription(
    userDetail,
    statusList = [constants.ACTIVE, constants.PAUSED],
    entitlementLevelList = [constants.ENTITLEMENTLEVEL_SINGLE, constants.BUNDLE, constants.ENTITLEMENTLEVEL_MULTI]
  ) {
    let hasSubscription = false;

    if (userDetail?.gpsSubscriptions) {
      hasSubscription = userDetail.gpsSubscriptions.some(
        subscription => (subscription?.status && statusList.includes(subscription.status))
          && subscription?.entitlementLevel && entitlementLevelList.includes(subscription.entitlementLevel)
      );
    }

    return hasSubscription;
  }

  /**
   * Method to get books by businessModelCode
   *
   * @param {object} bookShelf
   * @param {string} businessModelCode
   * @param {boolen} expired
   */
  // TODO: This function should adpot with all books not only for pplus
  static getProductByBMC(bookShelf, businessModelCode = [], expired = false) {
    const { books, bannerBook } = bookShelf;
    const originalBooks = bannerBook ? [...books, bannerBook] : [...books];
    return originalBooks.filter(book => book.product_entitlements
      && book.product_entitlements.business_model_code
      && businessModelCode.includes(book.product_entitlements.business_model_code)
      && book.product_entitlements.expired === expired
      && book?.gpsSubscriptionOfBook?.status === constants.ACTIVE);
  }

  /**
   * Function to decode URL
   * @param {string} uri
   * @param {number} mod
   */
  static decodeURIComponentSafe(uri, mod = 0) {
    let out = new String();
    let arr;
    let i = 0;
    let l;
    let x;
    arr = uri.split(/(%(?:d0|d1)%.{2})/);
    for (l = arr.length; i < l; i++) {
      try {
        x = decodeURIComponent(arr[i]);
      } catch (e) {
        x = mod ? arr[i].replace(/%(?!\d+)/g, '%25') : arr[i];
      }
      out += x;
    }
    return out;
  }

  static getNoOfDays(date1, date2) {
    const startDate = new Date(date1);
    const endDate = new Date(date2);
    const diffTime = startDate.getTime() - endDate.getTime();
    const noOfDays = Math.ceil(diffTime / (1000 * 3600 * 24));
    return noOfDays;
  }

  //  return true if user role is student else it is instructor
  static getUserRole(books) {
    let isStudent = true;
    if (books && Array.isArray(books) && books.length > 0) {
      isStudent = !books.some((title) => {
        let hasInstructor = false;
        if ((title.product_entitlements.user_role === constants.PDF_CONSTANTS.USER_ROLE.INSTRUCTOR)
          || (title.product_entitlements.user_role === constants.PDF_CONSTANTS.USER_ROLE.EDUCATOR)) {
          hasInstructor = true;
        }
        if (!hasInstructor && title.courses.length > 0) {
          hasInstructor = title.courses.some(course => (course.user_role === constants.PDF_CONSTANTS.USER_ROLE.INSTRUCTOR) || (course.user_role === constants.PDF_CONSTANTS.USER_ROLE.EDUCATOR));
        }
        return hasInstructor;
      });
    } else if (books) {
      const { role, products } = books;
      const cousreProdcut = (products && products.length) ? products[0] : {};
      if ((role && role !== constants.USER_ROLE_STUDENT) || (cousreProdcut.role && cousreProdcut.role !== constants.USER_ROLE_STUDENT)) {
        isStudent = false;
      }
    }
    return isStudent;
  }

  /**
   *
   * @param {Object} user
   * return menu array based on user
   */
  static getAccountMenuListItems(user, isGhostAccount = false, menuFlashMessage) {
    const language = Framework.getStoreRegistry().getStore('language');
    const isPPlusUser = this.isPPlusUser(user);
    const isSsoUser = !!user.socialLink;
    let accountMenuList = [];
    const helpCenterMenu = {
      title: language.getMessage('menu.helpCenter'),
      url: isPPlusUser ? env.ALGOLIA_SEARCH_CONFIG.helpCenter : env.HELP_CENTER_NON_MOJO,
      original: constants.ACCOUNT_SETTINGS_HELP_CENTER,
      isNewTarget: true,
      disabled: false
    };
    if (!isGhostAccount) {
      accountMenuList = [
        {
          title: language.getMessage('menu.myAccount'),
          subTitle: menuFlashMessage,
          url: env.ALGOLIA_SEARCH_CONFIG.accountUrl,
          original: constants.ACCOUNT_SETTINGS_MYACCOUNT,
          isNewTarget: false,
          disabled: false
        },
        {
          title: language.getMessage('menu.preferencesCenter'),
          url: env.ALGOLIA_SEARCH_CONFIG.preferencesCenter,
          original: constants.ACCOUNT_SETTINGS_PREFERENCES_CENTER, // original is used for Eventing - need to change the name of the attr
          isNewTarget: true,
          disabled: false
        },
        {
          title: !isSsoUser ? language.getMessage('device.ACCESS_CHANGE_PASSWORD') : language.getMessage('device.ACCESS_RESET_PASSWORD'),
          url: !isSsoUser ? env.ALGOLIA_SEARCH_CONFIG.changePassword : env.ALGOLIA_SEARCH_CONFIG.resetPassword,
          original: !isSsoUser ? constants.ACCOUNT_SETTINGS_CHANGE_PASSWORD : constants.ACCOUNT_SETTINGS_RESET_PASSWORD,
          isNewTarget: true,
          disabled: this.isInternalUser(user)
        }
      ];
    }
    accountMenuList = [...accountMenuList, helpCenterMenu];

    return accountMenuList;
  }

  /**
   * Returns true if gps subscription's status contains either ACTIVE or PAUSED
   * In authome we will get userSubscription and for ereader we will get productSubscription
   *
   * @param {Object} userOrProductSubscription
   * @returns
   */
  static isPPlusUser(userOrProductSubscription, location = constants.AUTH_HOME) {
    let isPPlusUser = false;

    if (location === constants.AUTH_HOME) {
      if (userOrProductSubscription && userOrProductSubscription.gpsSubscriptions) {
        isPPlusUser = userOrProductSubscription.gpsSubscriptions.some(subscription => subscription.status === constants.ACTIVE
          || subscription.status === constants.PAUSED);
      }
    } else {
      isPPlusUser = userOrProductSubscription?.status === constants.ACTIVE || userOrProductSubscription?.status === constants.PAUSED;
    }

    return isPPlusUser;
  }

  /**
   *
   * @param {string} flagName
   * @param {object} flags
   * @param {object} language
   * @param {language} language
   * return to show or hide the Mondly,Trubo,ITpro component
   */

  static hasShown = (flagName, flags, userdetails, featureFlagsClient) => {
    const { dob, studyLevel, homeCountryCode } = userdetails || {};
    let isShown = false;
    switch (flagName) {
      case constants.LD_COMPONENTS_NAME.TURBO: {
        const currentUserAge = CommonUtils.getAge(dob);
        const isHighSchoolStudent = studyLevel === constants.HIGH_SCHOOL_STUDENT;
        const isInstructor = studyLevel === constants.FACULTY_OR_INSTRUCTOR;
        const isRoleAndAgeNotSet = !studyLevel && currentUserAge === null;
        // User age should be greater then or equal to 18 , except if the user role and age is not specified or if the user is an instructor
        const isEligibleAge = (isInstructor || isRoleAndAgeNotSet) ? true
          : currentUserAge >= constants.MINIMUM_TURBO_ELIGIBLE_AGE;
        const isEligibleCountry = (homeCountryCode && homeCountryCode.toUpperCase()) === 'US';

        /* We show turbo if below logic are satisfied
        1) Turbo LD enabled
        2) And user country should be US
        3) AND user role/studylevel should not be HIGH_SCHOOL_STUDENT
        4) AND age should be greater then or equal to 18 ,
        except if the user role and age is not specified or if the user is an instructor
        */
        if (featureFlagsClient.variation(flagName, false)
          && isEligibleCountry
          && !isHighSchoolStudent
          && isEligibleAge) {
          isShown = true;
        }
        break;
      }
      default:
        isShown = featureFlagsClient.variation(flagName, false);
        break;
    }
    return isShown;
  };

  /**
   *
   * @returns footer menu list
   */
  static footerMenuList = (isGhostAccount = false, enableQRcodeFlag) => ({
    showAppIcon: !enableQRcodeFlag && !isGhostAccount,
    downloadApp: {
      url: env.BOOKSHELF_CONFIG.more.MOJO_APP
    },
    menuList: [{
      id: 'termsofuse',
      title: 'footer.termsOfUse',
      url: env.BOOKSHELF_CONFIG.more.MOJO_TERMS,
      target: '_blank',
      localeString: 'footerterms'
    },
    {
      id: 'privacy',
      title: 'footer.privacy',
      url: env.BOOKSHELF_CONFIG.more.MOJO_PRIVACY,
      target: '_blank',
      localeString: 'footerprivacy'
    },
    {
      id: 'cookies',
      title: 'footer.cookies',
      url: env.BOOKSHELF_CONFIG.more.MOJO_COOKIES,
      target: '_blank',
      localeString: 'footercookies'
    },
    {
      id: 'donotsellmypersonalinformation',
      title: 'footer.doNotSellMyPersonalInformation',
      url: env.BOOKSHELF_CONFIG.more.MOJO_COOKIES,
      target: '_blank',
      localeString: 'footerinfo'
    },
    {
      id: 'accessibility',
      title: 'footer.accessibility',
      url: env.BOOKSHELF_CONFIG.more.MOJO_ACCESSIBILITY,
      target: '_blank',
      localeString: 'footeraccessibility'
    },
    {
      id: 'patentnotice',
      title: 'footer.patentNotice',
      url: env.BOOKSHELF_CONFIG.more.MOJO_NOTICE,
      target: '_blank',
      localeString: 'footerpatentnotice'
    }
    ]
  });

  static algoliaConfig = () => (
    {
      appId: env.ALGOLIA_SEARCH_CONFIG.appId,
      apiKey: env.ALGOLIA_SEARCH_CONFIG.apiKey,
      productIndex: env.ALGOLIA_SEARCH_CONFIG.productIndex,
      categoryIndex: env.ALGOLIA_SEARCH_CONFIG.categoryIndex,
      algoliaUrl: env.BOOKSHELF_CONFIG.pmcSearch,
      accountUrl: env.ALGOLIA_SEARCH_CONFIG.accountUrl,
      changePassword: env.ALGOLIA_SEARCH_CONFIG.changePassword,
      resetPassword: env.ALGOLIA_SEARCH_CONFIG.resetPassword,
      helpCenter: env.ALGOLIA_SEARCH_CONFIG.helpCenter,
      searchQuery: env.ALGOLIA_SEARCH_CONFIG.searchQuery,
      preferencesCenter: env.ALGOLIA_SEARCH_CONFIG.preferencesCenter,
      searchBarUtmParams: constants.SEARCH_BAR_UTM_PARAM
    }
  );

  static initChatbotScript = (user, enableNewChatbot) => {
    const bookshelfConfig = env.BOOKSHELF_CONFIG;
    if (!enableNewChatbot) {
      if (this.isPPlusUser(user)) {
        if (!window.embedded_svc) {
          const s = document.createElement('script');
          s.async = true;
          s.setAttribute('src', bookshelfConfig.chatbot.url);
          // eslint-disable-next-line func-names
          s.onload = function () { Chatbot(null, bookshelfConfig.chatbot, enableNewChatbot); };
          document.body.appendChild(s);
        } else {
          const script1 = document.createElement('SCRIPT');
          script1.src = Chatbot('https://service.force.com', bookshelfConfig.chatbot, enableNewChatbot);
          script1.async = true;
          document.body.appendChild(script1);
        }
        this.createSessionMirror();
      }
    } else {
      window.addEventListener('onEmbeddedMessagingReady', async () => {
        try {
          const response = await fetch(`${bookshelfConfig.chatbot.generateTokenUrl}?tokentype=ppluschatbot&subject=${user.email ?? ''}`);
          const jwt = await response.text();
          window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
            identityTokenType: 'JWT',
            identityToken: jwt
          });
          window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            pearsonId: user.id
          });
        } catch (e) {
          console.log(e, '~onEmbeddedMessagingReady error');
        }
      });
      registerEmbeddedMessagingOpenChatFromQueryParam();
      if (this.isPPlusUser(user)) {
        if (!window.embeddedservice_bootstrap) {
          const s = document.createElement('script');
          s.async = true;
          s.setAttribute('src', bookshelfConfig.chatbot.eswUrl);
          // eslint-disable-next-line func-names
          s.onload = function () { Chatbot(null, bookshelfConfig.chatbot, enableNewChatbot); };
          document.body.appendChild(s);
        } else {
          Chatbot(null, bookshelfConfig.chatbot, enableNewChatbot);
        }
      }
    }
  };

  static createSessionMirror = () => {
    const objectsReady = (window.toolbar instanceof Object) && (window.piSession instanceof Object);

    if (!objectsReady || window.isPiSessionUsageNotPossible) {
      return;
    }

    Object.defineProperty(window.toolbar, 'piToken', {
      get: () => {
        let token = null;
        window.piSession.getToken((status, rToken) => {
          token = rToken;
        });
        return token;
      }
    });
    Object.defineProperty(window.toolbar, 'piUserId', {
      get: () => window.piSession.userId()
    });
  };

  static pageContainerTracker(flags = {}) {
    const reactHandler = this;
    const containersNodeLists = [];
    constants.TRACKING_CONTAINERS.forEach((querySelector) => {
      const ele = document.querySelector(querySelector);
      if (ele) {
        containersNodeLists.push(ele);
      }
    });
    const callback = function (entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const contanierDataOrder = entry.target.getAttribute('data-order');
          const containerTitle = entry.target.getAttribute('data-title');
          try {
            CommonUtils.viewChannelsViewPort(flags, entry.target);
          } catch (e) {
            console.log(e, '~on viewChannels card scroll event error');
          }
          if (contanierDataOrder) {
            // GA event for user contaniers
            const personId = window.piSession.userId();
            const customFields = { event_value: contanierDataOrder, personId, pageName: constants.AUTH_HOME_SCROLL_EVENT };
            CommonUtils.dispatchGaEvent(constants.AUTH_HOME_SCROLL_EVENT, constants.USER_CONTAINER, constants.USER_SEES_CONTAINER, containerTitle, customFields);
          }
          reactHandler.viewPortIntersecting(containerTitle, flags);

          CommonUtils.logBannerViewImpression(entry);

          // Log view impression for QR code test variants card
          CommonUtils.logQRCodeTestVariantViewEvent(entry.target);
        }
      });
    };
    if (!this.observer) {
      const options = {
        root: null,
        rootMargin: '-200px 0px -200px 0px',
        threshold: 0
      };
      this.observer = new window.IntersectionObserver(callback, options);
    } else {
      this.observer.disconnect();
    }
    containersNodeLists.forEach((target) => {
      reactHandler.observer.observe(target);
    });
  }

  /**
   * Observes AET instructor dashboard containers and fires insight viewport events.
   *
   * Mirrors `pageContainerTracker` but uses `dashboardObserver` so dashboard tracking
   * does not share or disconnect the auth-home `observer`. Queries
   * `TRACKING_CONTAINERS`, reconnects the observer on each call, and on intersection
   * delegates to `viewAetDashboardViewPort` (886 / 891 only).
   *
   * Typically invoked from `initDasboardPageTracker` after dashboard cards mount.
   *
   * @param {Object} [flags={}] Screen/feature flags passed to viewport event helpers.
   * @returns {void}
   */
  static dashboardPageContainerTracker(flags = {}) {
    const reactHandler = this;
    const containersNodeLists = [];
    constants.TRACKING_CONTAINERS.forEach((querySelector) => {
      const ele = document.querySelector(querySelector);
      if (ele) {
        containersNodeLists.push(ele);
      }
    });
    const callback = function (entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          try {
            CommonUtils.viewAetDashboardViewPort(flags, entry.target);
          } catch (e) {
            console.log(e, '~on AET dashboard viewport event error');
          }
        }
      });
    };
    if (!this.dashboardObserver) {
      const options = {
        root: null,
        rootMargin: '-200px 0px -200px 0px',
        threshold: 0
      };
      this.dashboardObserver = new window.IntersectionObserver(callback, options);
    } else {
      this.dashboardObserver.disconnect();
    }
    containersNodeLists.forEach((target) => {
      reactHandler.dashboardObserver.observe(target);
    });
  }

  static viewPortIntersecting = (locationInApp, flags) => {
    // eslint-disable-next-line camelcase
    const is_ghost_account = CommonUtils.isGhostAccount();
    const {
      ETEXT_BOOK_LABEL
    } = constants;
    const {
      LIBRARY,
      LIBRARYL2,
      LIBRARY_L2,
      COURSE_TAB
    } = constants.LOCATION_IN_APP;
    const {
      BOOK,
      BOOK_WITH_CHANNEL
    } = constants.HERO_BANNER_TYPES;
    const { AUDIO_QUERY_SELECTOR } = constants;
    const isLibraryAudioBook = !!(document.querySelector(AUDIO_QUERY_SELECTOR.LIBRARY));
    const HERO_BANNER_LABEL_MAP = {
      [BOOK]: ETEXT_BOOK_LABEL,
      [BOOK_WITH_CHANNEL]: ETEXT_BOOK_LABEL
    };

    switch (locationInApp) {
      case COURSE_TAB: {
        const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
        const heroBannerType = heroBanner ? getSnapshot(heroBanner).bannerType : '';

        if (heroBannerType === BOOK || heroBannerType === BOOK_WITH_CHANNEL) {
          CommonUtils.dispatchGaEvent(
            constants.AUTH_HOME_CATEGORY,
            constants.EVENT_AUTHHOMEEVENT,
            constants.BOOK_VIEW_EVENT_ACTION,
            HERO_BANNER_LABEL_MAP[heroBannerType],
            CommonUtils.getLibraryViewEventCustomFields(COURSE_TAB)
          );
        }
        break;
      }
      case LIBRARY: {
        const libraryEle = document.querySelector('.one-reader-lib-bookCard');
        const libarayAddTitleEle = document.querySelector('#one-reader-lib-addtitle');
        const libraryMarketingCardEle = document.querySelector('#one-reader-lib-etext-content-card');

        if (libraryEle) {
          CommonUtils.dispatchGaEvent(
            constants.AUTH_HOME_CATEGORY,
            constants.EVENT_AUTHHOMEEVENT,
            constants.BOOK_VIEW_EVENT_ACTION,
            ETEXT_BOOK_LABEL,
            CommonUtils.getLibraryViewEventCustomFields(LIBRARY)
          );
        }
        if (libarayAddTitleEle) {
          CommonUtils.dispatchGaEvent(
            constants.AUTH_HOME_CATEGORY,
            constants.EVENT_AUTHHOMEEVENT,
            constants.BOOK_VIEW_EVENT_ACTION,
            constants.ADD_TITLE_EVENT_LABELV1,
            CommonUtils.getLibraryViewEventCustomFields(LIBRARY)
          );
        }
        if (libraryMarketingCardEle) {
          CommonUtils.dispatchGaEvent(
            constants.AUTH_HOME_CATEGORY,
            constants.EVENT_AUTHHOMEEVENT,
            constants.BOOK_VIEW_EVENT_ACTION,
            constants.ETEXT_CONTENT_CARD,
            CommonUtils.getLibraryViewEventCustomFields(LIBRARY)
          );
        }
      }
        break;
      case LIBRARYL2:
        CommonUtils.dispatchGaEvent(
          constants.AUTH_HOME_CATEGORY,
          constants.EVENT_AUTHHOMEEVENT,
          constants.BOOK_VIEW_EVENT_ACTION,
          ETEXT_BOOK_LABEL,
          CommonUtils.getLibraryViewEventCustomFields(LIBRARY_L2, true)
        );
        break;
      default:
        break;
    }
  };

  static isEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  /**
   * @param {*} value
   * @returns {boolean} True for non-null object literals (excludes arrays).
   */
  static isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  static getBooksLength(books) {
    const booksLength = {
      activeBooksLength: 0,
      inactiveBooksLength: 0
    };
    const activeBooks = books && books.filter(book => (
      book.product_entitlements
      && !book.product_entitlements.expired && !book.course_expired
    ));

    const inactiveBooks = books && books.filter(book => book.product_entitlements
      && (book.product_entitlements.expired || book.course_expired));
    booksLength.activeBooksLength = activeBooks.length;
    booksLength.inactiveBooksLength = inactiveBooks.length;

    return booksLength;
  }

  static toString(value) {
    return ((value || value === 0) && value !== 'n/a') ? value.toString() : null;
  }

  static isInternalUser(user) {
    return user.socialLink && user.socialLink.toLowerCase() === 'internalazure';
  }

  /**
      * Function debounce for performance optimization while repeat excution
      * @param {function} fn
      * @param {millseconds} ms
      */
  static debounce(fn, ms = 500) {
    let timer;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => { fn.apply(this, args); }, ms);
    };
  }

  /**
   * To check screen resoultion below threshhold breakPoint
   * @param {*} breakPoint
   * @returns
   */
  static isScreenResoultionBelow = breakPoint => (((window.innerWidth || document.documentElement.clientWidth) < breakPoint));

  /**
   * Returns sorted books based on start date, active and inactive status
   * @param {*} breakPoint
   * @returns
   */
  static sortBooksOnStartDateAndStatus = (bookshelf) => {
    const updatedBookshelf = {};

    if (bookshelf.books && bookshelf.books.length > 0) {
      bookshelf.books.sort((a, b) => new Date(a.product_entitlements.start_date).getTime()
        - new Date(b.product_entitlements.start_date).getTime()).reverse();
      const activeBooks = bookshelf.books.filter(book => !book.course_expired && !book.product_entitlements.expired);
      const nonActiveBooks = bookshelf.books.filter(book => book.course_expired || book.product_entitlements.expired);
      updatedBookshelf.books = [...activeBooks, ...nonActiveBooks];
    }

    return updatedBookshelf;
  };

  /**
   * Update userpreference firstTimeUser
   *
   * @param {*} updateUserActivity
   */
  static updateUserPreference(updateUserActivity) {
    userPreferencesService.fetch().then(({ data }) => {
      const userActivity = data?.userActivity || {};
      const eventData = {
        eventType: 'PUT',
        payload: {
          userActivity: {
            ...userActivity,
            ...updateUserActivity
          }
        }
      };
      Framework.getEventManager().publish(constants.USER_PREFERENCES_REQUESTED, eventData);
    });
  }

  /**
   * Update userpreference firstTimeUser
   *
   * @param {*} payload
   */
  static getUserBookPreference(payload, onSuccess, onError) {
    userBookPreferencesService.fetch(payload).then(({ data }) => {
      onSuccess(data);
    }).catch((error) => {
      if (onError) {
        onError(error);
      }
    });
  }

  /**
   * To detect reader page came from Intergrated MLM launch or not
   */
  static isIntergratedMLMLaunch = () => {
    const mlmLaunchQueryParamValue = PathUtils.getQueryParameterbyName(constants.MLM_LAUNCH_EREADER);

    return !!(mlmLaunchQueryParamValue && mlmLaunchQueryParamValue.toLowerCase() === 'y');
  };

  /**
   * Handle post message from Integrated MLM Launch
   * @param {*} event
   * @param {*} listenerCallback
   */
  static handlePostMessageFromIntegratedMLMLaunch = (event, listenerCallback) => {
    if (event && event.data) {
      switch (event.data.type) {
        case constants.MLM_IA_LAUNCH:
          const {
            startPageId, endPageId, productId, courseId
          } = event.data;
          listenerCallback(startPageId || null);
          break;
        default:
          break;
      }
    }
  };

  /**
   * Initialize the communication between IntegratedMLM and one reader
   * @param {*} listenerCallback
   */

  static initializeIntegratedMLMLaunch = (listenerCallback) => {
    window.addEventListener('message', event => CommonUtils.handlePostMessageFromIntegratedMLMLaunch(event, listenerCallback));
  };

  /**
   * Close the communication between IntegratedMLM and one reader
   */

  static closeConnectionIntegratedMLMLaunch = (listenerCallback) => {
    window.removeEventListener('message', event => CommonUtils.handlePostMessageFromIntegratedMLMLaunch(event, listenerCallback));
  };

  /**
   * Method to find whether user has book title
   * @param {*} bookshelf
   * @returns
   */
  static hasBooks = (bookshelf = {}) => bookshelf.books && bookshelf.books.length > 0;

  /**
   * Method to find whether user has active book list
   *
   * @param {<LIST>} books
   * @returns
   */
  static getActiveBooks = (books = []) => (
    books.filter(book => (CommonUtils.isActiveBook(book)))
  );

  /**
   * Returns the inactive books
   * @param {Array} books - The books
   * @returns {Array} - The inactive books
   */
  static getInactiveBooks = (books = []) => (
    books.filter(book => (!CommonUtils.isActiveBook(book)))
  );

  /**
   * Method to find whether user has in active book listt
   *
   * @param {<LIST>} books
   * @returns
   */
  static getExpiredBooks = (books = []) => (
    books.filter(book => (CommonUtils.isExpiredBook(book)))
  );

  /**
      * Function to check isPPlusSubscriptionBook
      * @param {object} book
      */
  static isPPlusSubscriptionBook = (book, checkExpire = true) => {
    const {
      product_entitlements: {
        // eslint-disable-next-line camelcase
        business_model_code
      }
    } = book;
    const bookExpired = checkExpire && !CommonUtils.isActiveBook(book);

    // eslint-disable-next-line camelcase
    return ((business_model_code === constants.DIRECT_TO_CONSUMER || business_model_code === constants.DIRECT_TO_CONSUMER_PERPETUAL) && (!bookExpired));
  };

  /**
     * Function to check active/inactive book
     * @param {object} book
     */
  static isActiveBook = (book) => {
    const isPausedBook = book?.gpsSubscriptionOfBook?.status === constants.PAUSED;

    return (book.product_entitlements && !book.product_entitlements.expired && !book.course_expired && !isPausedBook);
  };

  /**
   * Returns true if book is expired
   */
  static isExpiredBook = book => (
    (book.product_entitlements && book.product_entitlements.expired) || book.course_expired
  );


  /**
   * Returns true if the course has an end date before today.
   * Missing end date means the course is considered active.
   *
   * @param {Object} book
   * @returns {boolean}
   */
  static isCourseEnded = (book) => {
    const courses = book?.courses || [];
    const course = courses[0];

    if (!course) return false;

    const courseEndDate = course?.course_end_date;

    if (!courseEndDate) return false;

    return courseEndDate < new Date().toISOString();
  };

  /**
   * Returns true if the course is expired.
   *
   * @param {Object} book
   * @returns {boolean} True if the course is expired, false otherwise
   */
  static isCourseExpired = (book) => !CommonUtils.isActiveBook(book) || CommonUtils.isCourseEnded(book);

  /**
   * Method to find whether user has active book title
   *
   * @param {<LIST>} books
   * @returns
   */
  static hasActiveBooks = (books = []) => {
    const activeBooks = CommonUtils.getActiveBooks(books);
    return activeBooks.length > 0;
  };

  /**
   * Method to find whether user has Inactive book title
   *
   * @param {<LIST>} books
   * @returns
   */
  static hasInActiveBooks = (books = []) => {
    const inActiveBooks = CommonUtils.getExpiredBooks(books);
    return inActiveBooks.length > 0;
  };

  /**
   * To identify whether user is ghost account or not
   *
   */
  static isGhostAccount() {
    const tpiValue = PathUtils.getQueryParameterbyName('isTpi');

    return (tpiValue && tpiValue.toLowerCase() === 'y') || false;
  }

  /**
   * To identify whether LMS query param is present or not
   *
   */
  static isLMSQueryParamAvailable() {
    const isLMS = PathUtils.getQueryParameterbyName(constants.IS_LMS);

    return (isLMS && isLMS.toLowerCase() === 'y') || false;
  }

  /**
   * Function which returns banner's expirationDate from GPS switchTitle/autoRenewal
   * based on bannerid
   *
   * @param {*} userGpsSubscriptions
   * @param {String} bannerId
   * @returns
   */
  static getBannerExpirationDate(userGpsSubscriptions, bannerId) {
    let endDate = null;

    if (userGpsSubscriptions && bannerId) {
      switch (bannerId) {
        case constants.SUBSCRIPTION_BANNER.AUTORENEWALCREDITCARD:
        case constants.SUBSCRIPTION_BANNER.AUTORENEWALBOOKSTORE: {
          if (userGpsSubscriptions.autoRenewal) {
            const last21Obj = userGpsSubscriptions.autoRenewal.find(({ period, type }) => (
              (period && (period.toLowerCase() === constants.SWAP_TITLE_PERIODS.LAST21.toLowerCase()))
              && (type && (type.toLowerCase() === bannerId.toLowerCase()))));
            endDate = last21Obj?.expirationDate;
          }
          break;
        }
        case constants.SUBSCRIPTION_BANNER.SWITCHTITLE: {
          if (userGpsSubscriptions.swapTitle) {
            const first14Obj = userGpsSubscriptions.swapTitle.find(({ period }) => period && period.toLowerCase() === constants.SWAP_TITLE_PERIODS.FIRST14.toLowerCase());
            endDate = first14Obj?.expirationDate;
          }
          break;
        }
        case constants.SUBSCRIPTION_BANNER.PAUSED: {
          if (userGpsSubscriptions) {
            endDate = userGpsSubscriptions.endDate;
          }
          break;
        }
        case constants.SUBSCRIPTION_BANNER.EXPIRED: {
          if (userGpsSubscriptions) {
            endDate = userGpsSubscriptions.endDate;
          }
          break;
        }
        default:
          break;
      }
    }

    return endDate;
  }

  /**
   * Browser locale for numeric date order (US mm/dd vs India dd/mm).
   * Uses the OS/browser locale, not the app language picker.
   *
   * @returns {string}
   */
  static getBrowserLocale = () => {
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      const { locale } = Intl.DateTimeFormat().resolvedOptions();

      if (locale) {
        return locale;
      }
    }

    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }

    return 'en-US';
  };

  /**
   * Formats a date as a locale numeric string (e.g. 03/15/2026 or 15/03/2026).
   * Does not change MMM DD, YYYY display dates.
   *
   * @param {Date|string|number} timeStamp
   * @param {string} [locale]
   * @returns {string}
   */
  static formatLocaleDateOnly = (timeStamp, locale) => {
    if (!timeStamp) {
      return '';
    }

    const date = new Date(timeStamp);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const resolvedLocale = locale || CommonUtils.getBrowserLocale();

    return date.toLocaleDateString(resolvedLocale, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  /**
   * Empty-field hint matching the locale numeric date order.
   *
   * @param {string} [locale]
   * @param {string} [yearToken='yyyy'] - 'yyyy' (assignments) or 'yy' (course settings)
   * @returns {string} e.g. mm/dd/yyyy or dd/mm/yy
   */
  static getLocaleDatePlaceholder = (locale, yearToken = 'yyyy') => {
    const resolvedLocale = locale || CommonUtils.getBrowserLocale();
    const parts = new Intl.DateTimeFormat(resolvedLocale).formatToParts(new Date(2000, 11, 31));

    return parts.map((part) => {
      switch (part.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'mm';
        case 'year':
          return yearToken;
        default:
          return part.value;
      }
    }).join('');
  };

  /**
   * react-datepicker token pattern for the locale numeric date order.
   *
   * @param {string} [locale]
   * @param {string} [yearToken='yyyy'] - 'yyyy' (assignments) or 'yy' (course settings)
   * @returns {string} e.g. MM/dd/yyyy or dd/MM/yy
   */
  static getLocaleDateFormat = (locale, yearToken = 'yyyy') => {
    const resolvedLocale = locale || CommonUtils.getBrowserLocale();
    const parts = new Intl.DateTimeFormat(resolvedLocale).formatToParts(new Date(2000, 11, 31));

    return parts.map((part) => {
      switch (part.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return yearToken;
        default:
          return part.value;
      }
    }).join('');
  };

  static US_NUMERIC_DATE_PLACEHOLDER = 'mm/dd/yyyy';

  static US_NUMERIC_DATE_FORMAT = 'MM/dd/yyyy';

  /**
   * FME flag: locale numeric dates vs US mm/dd/yyyy (from context.state.flags).
   *
   * @param {Object} [flags]
   * @returns {boolean}
   */
  static isLocaleDateFormattingEnabled = (flags) => Boolean(flags?.enableLocaleDateInputFormat);

  /**
   * Numeric date placeholder for date pickers (locale vs US mm/dd/yyyy).
   *
   * @param {boolean} [enableLocaleDateInputFormat]
   * @param {string} [locale]
   * @param {string} [yearToken='yyyy']
   * @returns {string}
   */
  static resolveNumericDatePlaceholder = (
    enableLocaleDateInputFormat,
    locale,
    yearToken = 'yyyy'
  ) => {
    if (!enableLocaleDateInputFormat) {
      return yearToken === 'yy' ? 'mm/dd/yy' : CommonUtils.US_NUMERIC_DATE_PLACEHOLDER;
    }

    return CommonUtils.getLocaleDatePlaceholder(locale, yearToken);
  };

  /**
   * react-datepicker pattern for numeric dates (locale vs US mm/dd/yyyy).
   *
   * @param {boolean} [enableLocaleDateInputFormat]
   * @param {string} [locale]
   * @param {string} [yearToken='yyyy']
   * @returns {string}
   */
  static resolveNumericDateFormat = (
    enableLocaleDateInputFormat,
    locale,
    yearToken = 'yyyy'
  ) => {
    if (!enableLocaleDateInputFormat) {
      return yearToken === 'yy' ? 'MM/dd/yy' : CommonUtils.US_NUMERIC_DATE_FORMAT;
    }

    return CommonUtils.getLocaleDateFormat(locale, yearToken);
  };

  /**
   * Formats a calendar date as mm/dd/yyyy (US) or locale numeric string.
   *
   * @param {Date|string|number} timeStamp
   * @param {boolean} [enableLocaleDateInputFormat]
   * @param {string} [locale]
   * @returns {string}
   */
  static resolveNumericDateOnly = (timeStamp, enableLocaleDateInputFormat, locale) => {
    if (!timeStamp) {
      return '';
    }

    const date = new Date(timeStamp);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    if (!enableLocaleDateInputFormat) {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    }

    return CommonUtils.formatLocaleDateOnly(timeStamp, locale);
  };

  /**
   * To get the browser timeZone date format
   *
   * @param {<DATE|UTC>} timeStamp
   * @returns
   */
  static formatDate = (timeStamp = null, format = null, flags) => {
    const date = timeStamp ? new Date(timeStamp) : new Date();

    if (format === constants.MONTH_DAY_YEAR_FORMAT) {
      return CommonUtils.resolveNumericDateOnly(
        date,
        CommonUtils.isLocaleDateFormattingEnabled(flags)
      );
    }
    if (format === constants.YEAR_MONTH_DAY_FORMAT) {
      const year = date.getFullYear();
      const month = (`0${date.getMonth() + 1}`).slice(-2);
      const day = (`0${date.getDate()}`).slice(-2);

      return `${year}-${month}-${day}`;
    }
    const monthList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = (`0${date.getDate()}`).slice(-2);
    const month = monthList[date.getMonth()];
    const year = date.getFullYear();
    const formatDateString = { MMM: month, DD: day, YYYY: year };

    return constants.DATE_FORMAT.replace(/MMM|DD|YYYY/g, match => (formatDateString[match]));
  };

  /**
   * Formats an assignment due date for display: date, time, and browser timezone.
   *
   * @param {Object} assignment
   * @returns {String}
   */
  static formatAssignmentDueDate = (assignment) => {
    const dueDate = assignment?.dueDate || assignment?.dateDue || assignment?.date;

    if (!dueDate) {
      return '';
    }

    const timeZoneLabel = dateTimeUtils.getTzLabelFromOptions(
      dateTimeUtils.getBrowserTimezone(),
      dateTimeUtils.buildTimeZoneOptions()
    );
    const formattedDate = CommonUtils.formatDate(dueDate);
    const formattedTime = dateTimeUtils.formatDisplayTime(dueDate, assignment?.time);
    const displayTime = formattedTime && timeZoneLabel
      ? `${formattedTime} ${timeZoneLabel}`
      : formattedTime;

    return [formattedDate, displayTime].filter(Boolean).join(' ');
  };

  /**
   * returns the age based on DOB
   *
   * @param {*} dob
   * @returns
   */
  static getAge = (dob) => {
    if (dob) {
      const today = new Date();
      const birthDay = new Date(dob);
      const currentMonth = today.getMonth();
      const birthMonth = birthDay.getMonth();
      const currentDate = today.getDate();
      const birthDate = birthDay.getDate();
      const isCurrentMonthLessThanBirthMonth = currentMonth < birthMonth;
      const isSameMonthAndCurrentDteLessThanBirthDte = (currentMonth === birthMonth) && (currentDate < birthDate);
      let age = today.getFullYear() - birthDay.getFullYear();

      /**
       * If the current month is lesser than user's birth month then we subtract -1 year
       * If the current month and the user's birth month is same, And current date is lesser than user's birth date
       * then we subtract -1 year
       */
      age = (isCurrentMonthLessThanBirthMonth || isSameMonthAndCurrentDteLessThanBirthDte) ? age - 1 : age;

      return age;
    }

    return null;
  };

  /**
   * Returns book's product entitlement.
   *
   * @param {Object} book
   * @returns
   */
  static getProductEntitlementOfBook = book => book?.product_entitlements || {};

  /*
   * Returns matching gpsSubscription object based on subscription id of the book
   *
   * @param {String} subscriptionId
   * @param {Object} user
   * @returns
   */
  static getMatchingGpsSubscriptionOfBook = (subscriptionId, user) => {
    let bookSubscription;

    if (subscriptionId && user && user.gpsSubscriptions) {
      bookSubscription = user.gpsSubscriptions.find(subscription => subscription.subscriptionId === subscriptionId);
    }

    return bookSubscription || {};
  };

  /**
   * Returns the necessary IA (Inclusive Access) launch query parameters
   *
   * @returns
   */
  static getDecodedIAqueryParam = () => {
    let isIA = false;
    let IAProdcutID = null;
    let decodedQueryParam = '';
    const encodedQueryParam = PathUtils.getQueryParameterbyName(constants.IA_QUERY_PARAMS.REF);
    if (encodedQueryParam) {
      // eslint-disable-next-line no-undef
      decodedQueryParam = atob(encodedQueryParam);
      if (decodedQueryParam && decodedQueryParam.length > 0) {
        const isIAQueryParam = PathUtils.getQueryParameterbyName(constants.IA_QUERY_PARAMS.ISIA, decodedQueryParam);
        isIA = !!(isIAQueryParam && isIAQueryParam.toLowerCase() === 'y');
        IAProdcutID = PathUtils.getQueryParameterbyName(constants.IA_QUERY_PARAMS.PRODUCTID, decodedQueryParam) || null;
      }
    }

    return {
      isIA,
      IAProdcutID
    };
  };

  /**
   * Get IA (Inclusive access) book if the productId of the book
   * is available and isIA is equal to y in query param
   *
   * @param {*} books
   * @param {*} IAbookProductID
   * @returns
   */
  static getIAbookIfAvailable = (books) => {
    let IAbook;
    const { isIA, IAProdcutID } = CommonUtils.getDecodedIAqueryParam();
    const isIAandHasProductId = !!(isIA && IAProdcutID);

    if (isIAandHasProductId) {
      IAbook = books.find(book => book.product_id === IAProdcutID);
    }

    return IAbook;
  };

  static getIds = (id) => {
    let productId = null;
    let bookId = null;
    let courseId = null;

    if (typeof id === 'string' && id.includes(':')) {
      const ids = id.split(':');

      bookId = ids[0];
      productId = ids[1];
      courseId = ids[2];
    }

    return {
      productId,
      bookId,
      courseId
    };
  };

  /**
   * Get IA (Inclusive access) book if the productId of the book
   * is available and isIA is equal to y in query param
   *
   * @param {*} books
   * @param {*} IAbookProductID
   * @returns
   */
  static getIAbookIdxIfAvailable = (courses) => {
    let IAbookIdx = -1;
    const { isIA, IAProdcutID } = CommonUtils.getDecodedIAqueryParam();
    const isIAandHasProductId = !!(isIA && IAProdcutID);

    if (isIAandHasProductId) {
      IAbookIdx = courses.findIndex((course) => {
        CommonUtils.moveBookToFirstPosition(course, { productId: IAProdcutID });
        const { productId } = (course && course.collection && course.collection[0]) || {};

        if (productId) {
          return productId === IAProdcutID;
        }

        return false;
      });
    }

    return IAbookIdx;
  };

  /**
   * If course pill has more than one book associated.
   * Moves the deeplink book to first position.
   *
   * @param {Object} course
   * @param {Object} data
   */
  static moveBookToFirstPosition = (course, data = {}) => {
    let collectionIdx = 0;
    const courseId = data?.courseId || null;
    const bookId = data?.bookId || null;
    const productId = data?.productId || null;
    const hasMoreThanOneCourse = course?.collection?.length > 1;

    if (hasMoreThanOneCourse) {
      collectionIdx = course.collection.findIndex(
        book => (productId === book?.productId && bookId === book?.bookId && courseId === book?.courseId)
      );
    }

    if (hasMoreThanOneCourse && collectionIdx !== 0) {
      const deepLinkBook = course.collection[collectionIdx];

      if (deepLinkBook) {
        course.collection.splice(collectionIdx, 1);
        CommonUtils.setCourseImageTitle(course, deepLinkBook);
        // eslint-disable-next-line no-param-reassign
        course.collection = [deepLinkBook, ...(course.collection || [])];
      }
    }
  };

  /**
   * Map the title and coverImage for course
   *
   * @param {*} course
   * @param {*} obj
   */
  static setCourseImageTitle(course, { title, bookCoverImage }) {
    // eslint-disable-next-line no-param-reassign
    course.imageSrc = bookCoverImage;
    // eslint-disable-next-line no-param-reassign
    course.title = title;
  }

  static getMLMbookIdxIfAvailable = (courses, match, isMLMroute = false) => {
    let MLMBookIdx = -1;
    const mlmBookData = CommonUtils.getMLMRouteBookData(match) || {};
    const mlmCourseId = mlmBookData?.courseId || null;
    const mlmBookId = mlmBookData?.bookId || null;

    if (isMLMroute) {
      MLMBookIdx = courses.findIndex((course) => {
        CommonUtils.moveBookToFirstPosition(course, mlmBookData);
        const { bookId = null, courseId = null } = (course && course.collection && course.collection[0]) || {};

        if (mlmBookId && mlmCourseId) {
          return (bookId === mlmBookId) && (courseId === mlmCourseId);
        }
        if (mlmBookId) {
          return bookId === mlmBookId;
        }
        if (mlmCourseId) {
          return courseId === mlmCourseId;
        }

        return false;
      });
    }

    return MLMBookIdx;
  };

  /**
   * Checks for business model code either DTC or DTC Inclusive Access Or HEI book
   *
   * @param businessModelCode
   * @returns
   */
  static isPplusOrIAbookOrHEI = (businessModelCode, hasCourseId = false) => {
    const validCodes = new Set([
      constants.DIRECT_TO_CONSUMER,
      constants.DIRECT_TO_CONSUMER_IA,
      constants.DIRECT_TO_CONSUMER_PERPETUAL,
      ...constants.HEI_BMC_CODES_LIST
    ]);
    return businessModelCode && validCodes.has(businessModelCode) && !hasCourseId;
  };

  /**
  * Returns true if all the books are PPlus
  *
  * @param {List} books
  */
  static isAllPPlusBook(books) {
    return books.length > 0 ? books.every(book => CommonUtils.isPPlusSubscriptionBook(book, false)) : false;
  }

  /**
   * Returns true if the book is a bookstore book
   *
   * @param {Object} book
   * @returns
   */
  static isBookStorePPlusEntitlements = book => (book?.gpsSubscriptionOfBook?.subscriptionSource
    === constants.CHANNEL_PARTNER) && CommonUtils.isPPlusSubscriptionBook(book);

  /**
   * Returns true if all the books have the same BMCs
   *
   * @param {*} books
   * @param {*} bmcList
   * @returns {boolean}
   */
  static isAllBookSameBMCs(books, bmcList) {
    return books.length > 0 ? books.every(book => this.isAllowedBMC(book, bmcList)) : false;
  }

  /**
   * Returns true if subscriptionId is matched with any books
   *
   * @param {string} subscriptionId
   * @param {<LIST>} books
   * @returns
   */
  static isSubscriptionIdFoundInBooks = (subscriptionId, books) => {
    const isSubscriptionFound = books.find((book) => {
      const { subscription_id: productSubscriptionId } = CommonUtils.getProductEntitlementOfBook(book);

      return subscriptionId === productSubscriptionId;
    });

    return !!isSubscriptionFound;
  };

  /**
   * Returns true if all active bookstore subscriptions have pplus entitlements
   *
   * @param {Object} user
   * @param {List} books
   * @returns
   */
  static isAllActiveBookStoreHavePPlusEntitlements(user, books) {
    let isAllActiveBookStoreHavePPlusEntitlements = false;

    if (user && user.gpsSubscriptions && books) {
      const channelPartnerSubscriptions = user.gpsSubscriptions.filter(
        subscription => subscription?.subscriptionSource === constants.CHANNEL_PARTNER
          && (subscription?.status === constants.ACTIVE)
          && (subscription?.entitlementLevel === constants.ENTITLEMENTLEVEL_SINGLE
            || subscription?.entitlementLevel === constants.BUNDLE
            || subscription?.entitlementLevel === constants.ENTITLEMENTLEVEL_MULTI)
      );
      if (channelPartnerSubscriptions.length > 0) {
        isAllActiveBookStoreHavePPlusEntitlements = channelPartnerSubscriptions.every(({ subscriptionId }) => CommonUtils.isSubscriptionIdFoundInBooks(subscriptionId, books));
      }
    }

    return isAllActiveBookStoreHavePPlusEntitlements;
  }

  /**
   * Get propery value from an object
   * @param {*} obj
   * @param {*} name
   * @param {*} defaultValue
   * @returns
   */
  static getObjectPropertyValue = (obj, name, defaultValue) => (obj
    && Object.prototype.hasOwnProperty.call(obj, name) ? obj[name] : defaultValue);

  /**
   * Function to retrieve value for the container name and property name provided
   * @param {*} stackOrderData
   * @param {*} containerName
   * @param {*} propertyName
   * @param {*} defaultValue
   * @returns
   */
  static getContainerData(stackOrderData, containerName, propertyName, defaultValue = null) {
    const templateData = stackOrderData?.[containerName];
    const containerInfo = templateData || constants.DEFAULT_CONTAINER_DATA[containerName];
    let value = defaultValue;

    if (containerInfo) {
      if (propertyName.indexOf('.') !== -1) {
        const propertyNameList = propertyName.split('.');
        value = propertyNameList.reduce((previousValue, currentValue) => {
          let result;

          if (typeof previousValue !== 'undefined' && Object.prototype.hasOwnProperty.call(previousValue, currentValue)) {
            result = previousValue[currentValue];
          }

          return result;
        }, containerInfo);
      } else if (Object.prototype.hasOwnProperty.call(containerInfo, propertyName)) {
        value = containerInfo[propertyName];
      }
    }

    return value;
  }

  /**
   * Returns true if user has subscription with provided source
   * and has a subscription with its start date greater than the provided date
   *
   * @param {object} user
   * @param {string} source
   * @param {string} startDate
   * @param {Array} statusList
   * @returns
   */
  static hasSubscriptionWithSourceStartDateRange(
    user, source, startDate, statusList = [constants.ACTIVE, constants.PAUSED]
  ) {
    return user.gpsSubscriptions.some(
      subscription => subscription.subscriptionSource === source
        && (subscription?.status && statusList.includes(subscription.status))
        && (subscription?.entitlementLevel === constants.ENTITLEMENTLEVEL_SINGLE
          || subscription?.entitlementLevel === constants.BUNDLE
          || subscription?.entitlementLevel === constants.ENTITLEMENTLEVEL_MULTI)
        && CommonUtils.getNoOfDays(subscription.startDate, startDate) > 0
    );
  }

  /**
   * To local string compare
   *
   * @param {*} stringA
   * @param {*} stringB
   * @returns
   */
  static localStringCompare(stringA, stringB) {
    let isSame = false;
    if (typeof stringA === 'string' && typeof stringB === 'string') {
      isSame = stringA.toLowerCase().trim() === stringB.toLowerCase().trim();
    }

    return isSame;
  }

  /**
   * Returns true if the given businessModelCode matches with Integrated SMS bmc's
   *
   * @param {String} businessModelCode
   * @returns
   */
  static isIntegratedBMC(businessModelCode) {
    const integratedBMC = [constants.INTEGRATED_SMS, constants.INTEGRATED_RUMBA, constants.CG_RENEWALS];

    return integratedBMC.includes(businessModelCode);
  }

  /**
   * Returns true based on audio_book_supported flag from bookshelf
   *
   * @param {String} audioFlag
   * @returns
   */
  static isAudioBook(audioFlag) {
    const {
      AUDIO_FLAG_ON,
      AUDIO_FLAG_ON_BETA
    } = constants;

    if (PathUtils.getQueryParameterbyName('isTtsEnabled') === 'true') {
      return true;
    }

    return (audioFlag === AUDIO_FLAG_ON || audioFlag === AUDIO_FLAG_ON_BETA);
  }

  /**
   * Returns true if user has a subscription with the provided entitlementLevel
   *
   * @param {Object} userDetail
   * @param {Array} entitlementLevelList
   */
  static hasSubscriptionBasedOnEntitlementLevel(
    userDetail,
    entitlementLevelList = [
      constants.ENTITLEMENTLEVEL_SINGLE,
      constants.BUNDLE,
      constants.ENTITLEMENTLEVEL_MULTI
    ],
    statusList = [constants.ACTIVE]
  ) {
    return userDetail.gpsSubscriptions.some(
      subscription => (subscription?.entitlementLevel && entitlementLevelList.includes(subscription.entitlementLevel))
        && (subscription?.status && statusList.includes(subscription.status))
    );
  }

  /**
   * Get session storage
   *
   * @param {*} key
   * @param {*} parseData
   * @returns
   */
  static getSessionStorge(key, parseData = false) {
    let data = null;
    const store = window.sessionStorage.getItem(key);
    if (store) {
      data = parseData ? JSON.parse(store) : store;
    }

    return data;
  }

  /**
   * Set session storage
   *
   * @param {*} key
   * @param {*} data
   * @param {*} stringify
   */
  static setSessionStorge(key, data, stringify = false) {
    window.sessionStorage.setItem(key, stringify ? JSON.stringify(data) : data);
  }

  /**
   * Clear session storage
   *
   * @param {*} key
   */
  static clearSessionStorge(key) {
    if (this.getSessionStorge(key)) {
      window.sessionStorage.removeItem(key);
    }
  }

  /**
   * Get local storage
   *
   * @param {*} key
   * @param {*} parseData
   * @returns
   */
  static getLocalStorge(key, parseData = false) {
    try {
      let data = null;
      const store = window.localStorage.getItem(key);
      if (store) {
        data = parseData ? JSON.parse(store) : store;
      }

      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(`Could not read from localStorage for key: ${key}`, error);

      return null;
    }
  }

  /**
   * Set local storage
   *
   * @param {*} key
   * @param {*} data
   * @param {*} stringify
   */
  static setLocalStorge(key, data, stringify = false) {
    window.localStorage.setItem(key, stringify ? JSON.stringify(data) : data);
  }

  /**
   * Clear local storage
   *
   * @param {*} key
   */
  static clearLocalStorge(key) {
    if (CommonUtils.getLocalStorge(key)) {
      window.localStorage.removeItem(key);
    }
  }

  /**
   * Function to determine new user or exisiting user
   * User first time login will set current session id into sessionStorge incase for isFirstLogin not present
   * User Secondtime login will compare the current session id vs pervious session id from sessionStorge incase of isFirstLogin as false
   *
   * @param {*} userActivity
   * @param {*} user
   * @returns
   */
  static isExistingUser(userActivity) {
    const isFirstLogin = CommonUtils.getObjectPropertyValue(userActivity, 'isFirstLogin', true);
    const currentSessionId = window.piSession.getContextId();
    let isNewUser = isFirstLogin;
    const previousSessionId = this.getSessionStorge(constants.PEARSON_SESSION);
    if (isFirstLogin) { // First visit new user
      this.setSessionStorge(constants.PEARSON_SESSION, currentSessionId);
    } else if (!isFirstLogin && previousSessionId && (previousSessionId === currentSessionId)) { // existing user refresh in same session
      isNewUser = true;
    } else if (!isFirstLogin && previousSessionId && (previousSessionId !== currentSessionId)) { // Second Visit existing user
      this.clearSessionStorge(constants.PEARSON_SESSION);
    }

    return isNewUser;
  }

  /**
   * Return true if last accessed channel exists in location response
   * @returns
   */
  static hasLastAccessedChannel() {
    const location = getSnapshot(Framework.getStoreRegistry().getStore('lastlocation'));

    return !!(location && location.userChannelLocation && location.userChannelLocation.length > 0);
  }

  static getFullStoryScript = () => {
    const script = `window._fs_host="fullstory.com",
      window._fs_script="edge.fullstory.com/s/fs.js",
      window._fs_org="o-1NN4YM-na1",
      window._fs_namespace="FS",
      function(n,e,t,o,s,c,i,f){
        t in n?n.console&&n.console.log&&n.console.log('FullStory namespace conflict. Please set window["_fs_namespace"].')
        :((i=n[t]=function(n,e,t){i.q?i.q.push([n,e,t]):i._api(n,e,t)}).q=[],
        (c=e.createElement(o)).async=1,c.crossOrigin="anonymous",
        c.src="https://"+_fs_script,
        (f=e.getElementsByTagName(o)[0]).parentNode.insertBefore(c,f),
        i.identify=function(n,e,t){i(s,{uid:n},t),e&&i(s,e,t)},
        i.setUserVars=function(n,e){i(s,n,e)},
        i.event=function(n,e,t){i("event",{n:n,p:e},t)},
        i.anonymize=function(){i.identify(!1)},
        i.shutdown=function(){i("rec",!1)},
        i.restart=function(){i("rec",!0)},i.log=function(n,e){i("log",[n,e])},
        i.consent=function(n){i("consent",!arguments.length||n)},
        i.identifyAccount=function(n,e){c="account",(e=e||{}).acctId=n,
        i(c,e)},i.clearUserCookie=function(){},
        i.setVars=function(n,e){i("setVars",[n,e])},
        i._w={},f="XMLHttpRequest",i._w[f]=n[f],
        f="fetch",i._w[f]=n[f],
        n[f]&&(n[f]=function(){return i._w[f].apply(this,arguments)}),
        i._v="1.3.0")}(window,document,window._fs_namespace,"script","user")`;

    return script;
  };

  /**
   * Handles Info book cta events
   */
  static handleInfoBookCTAEvents = (
    book,
    isClicked = true,
    locationInApp = constants.LOCATION_IN_APP.COURSE_TAB,
    isFromInfo = true
  ) => {
    let eventLabel = null;
    let eventAction = null;
    const { LIBRARY_EVENT, LOCATION_IN_APP, EVENT_AUTHHOMEEVENT } = constants;
    const isLastAccessed = !!(book.last_activity);
    if (isFromInfo) { // Hero Inof CTA Click and hover event
      eventLabel = (isLastAccessed && locationInApp === LOCATION_IN_APP.COURSE_TAB)
        ? LIBRARY_EVENT.INFO.CONTINUE_READING
        : LIBRARY_EVENT.INFO.OPEN;
      eventAction = LIBRARY_EVENT.ETEXT_FEATURE_ELEMENT_CLICK_EVENT_ACTION;
    }
    const customFields = {
      is_ghost_account: CommonUtils.isGhostAccount(),
      location_in_app: locationInApp
    };

    if (eventAction && eventLabel && isClicked) {
      CommonUtils.dispatchGaEvent(
        LIBRARY_EVENT.EVENT_CATEGORY,
        EVENT_AUTHHOMEEVENT,
        eventAction,
        eventLabel,
        customFields
      );
    }
  };

  /**
   * To identify whether query param is present or not with actual value
   *
   * @param {*} query
   * @param {*} value
   * @param {*} searchParams
   * @returns
   */
  static isQueryParamAvailable(query, value, searchParams = null) {
    const isQueryExist = PathUtils.getQueryParameterbyName(query, searchParams);

    return (isQueryExist && isQueryExist.toLowerCase() === value) || false;
  }

  /**
   * Is the book of product_entitlements end date crossed the current date after N number of days
   *
   * @param {*} book
   * @param {*} numOfDays
   * @returns
   */
  static isBookEndDateWithinXDays(book, numOfDays) {
    // TODO : Need to check date difference on bookshelf mobx store itself by introduceing new propery
    let isValidBook = true;
    if (book?.product_entitlements?.end_date) {
      // Getting number of days between current days against book end date
      const actualNumOfDays = this.getNoOfDays(new Date(), book.product_entitlements.end_date);
      isValidBook = actualNumOfDays <= numOfDays;
    }

    return isValidBook;
  }

  /**
   * Returns true when the book has temporary access payment method.
   *
   * @param {*} book
   * @returns {boolean}
   */
  static isTempAccessBook(book) {
    return this.localStringCompare(
      book?.product_entitlements?.paymentMethodType,
      constants.TEMP_ACCESS_PAYMENT_METHOD
    );
  }

  /**
   * Returns true when the book belongs to a K12 business model.
   *
   * @param {*} book
   * @returns {boolean}
   */
  static isK12Book(book) {
    return this.isAllowedBMC(book, constants.K12_BMC_LIST);
  }

  /**
   * courseWare BMC (Console / courseware titles).
   *
   * @param {*} book
   * @returns {boolean}
   */
  static isCourseWareBook(book) {
    return this.localStringCompare(
      book?.product_entitlements?.business_model_code,
      constants.COURSE_WARE
    );
  }

  /**
   * Returns the List of book exclude expired book crossed Max days
   *
   * @param {*} books
   * @param {*} numOfDays
   * @returns
   */
  static removeExpiredBookAfterXDays(books, numOfDays) {
    // Filtering the non expired books as first things or expired book with in x days
    const bookList = books.filter(book => (!this.isExpiredBook(book) || this.isBookEndDateWithinXDays(book, numOfDays)));

    return bookList;
  }

  /**
   * Get last accessed channel from location
   *
   * @returns
   */
  static getRecentlyUsedChannel = () => {
    const location = getSnapshot(Framework.getStoreRegistry().getStore('lastlocation'));
    const channel = location && location.userChannelLocation && location.userChannelLocation[0];

    return channel;
  };

  /**
   * Returns recommended assets based on custom mapping
   *
   * @param {*} response
   * @returns
   */
  static getRecommendedAssetsData = (response) => {
    const recommendedAssetsData = {};
    const {
      PRACTICE_SET_ICON,
      HRS,
      MIN,
      ASSET_CARD_SECTION_COLOR,
      ASSET_CONTENT_TYPE_DISPLAY_TEXT,
      CONCEPT_THUMBNAIL
    } = constants;
    const { MAPPEDCHANNELASSET } = constants.MAPPED_CHANNEL_TEMPLATES;
    const {
      EXAMPLE, MULTIPLE_CHOICE, SIMPLE_QUESTIONS, CONCEPT, VIDEO_STREAM
    } = constants.ASSET_CONTENT_TYPE;
    const language = Framework.getStoreRegistry().getStore('language');

    if (response) {
      recommendedAssetsData.subTitle = (response.chapterTitle && response.topicTitle)
        ? `${response.chapterTitle} • ${response.topicTitle}` : (response.chapterTitle || response.topicTitle);
      const isAssetTypeConcept = CommonUtils.localStringCompare(response.assetType, CONCEPT);
      const isAssetTypeVideoStream = CommonUtils.localStringCompare(response.assetType, VIDEO_STREAM);

      if (typeof response.assetType === 'string'
        && (response.assetType.toLowerCase() === MULTIPLE_CHOICE
          || response.assetType.toLowerCase() === SIMPLE_QUESTIONS)) {
        recommendedAssetsData.description = ASSET_CONTENT_TYPE_DISPLAY_TEXT[response.assetType.toLowerCase()];
        recommendedAssetsData.thumbnailUrl = PRACTICE_SET_ICON;
        recommendedAssetsData.section = language.getMessage('hero.practiceProblem');
        recommendedAssetsData.sectionColor = ASSET_CARD_SECTION_COLOR.GREEN;
        recommendedAssetsData.backgroundColor = ASSET_CARD_SECTION_COLOR.BG_PRACTICE;
        recommendedAssetsData.icon = null;
        recommendedAssetsData.title = response.topicTitle || '';
      } else {
        if (!isAssetTypeConcept) {
          recommendedAssetsData.description = (response?.assetDuration?.split(':').length > 2)
            ? response?.assetDuration?.replace(/^0/, '').concat(HRS) : response?.assetDuration?.replace(/^0/, '').concat(MIN);
        }
        recommendedAssetsData.thumbnailUrl = response.thumbUrl
          || (isAssetTypeConcept ? CONCEPT_THUMBNAIL : '');
        recommendedAssetsData.section = response.assetContentType || '';
        recommendedAssetsData.sectionColor = (response.assetContentType === EXAMPLE) ? ASSET_CARD_SECTION_COLOR.PINK : ASSET_CARD_SECTION_COLOR.BLUE;
        recommendedAssetsData.backgroundColor = (response.assetContentType === EXAMPLE) ? ASSET_CARD_SECTION_COLOR.BG_EXAMPLE : ASSET_CARD_SECTION_COLOR.BG_CONCEPT;
        if (recommendedAssetsData.thumbnailUrl === CONCEPT_THUMBNAIL) {
          recommendedAssetsData.sectionColor = ASSET_CARD_SECTION_COLOR.YELLOW;
        }
        recommendedAssetsData.icon = MAPPEDCHANNELASSET.ICONS.PLAYICON;
        recommendedAssetsData.title = response.assetTitle || '';
        recommendedAssetsData.isVideo = isAssetTypeVideoStream;
      }
    }

    return recommendedAssetsData;
  };

  /**
   * Returns true when AI chat bot  is enabled
   *
   * @param {*} showAITutor
   * @param {*} isAIReviewUser
   * @param {*} isAITutorSupported
   * @returns
   */
  static isAITutorEnabled(showAITutor, isAIReviewUser, isAITutorSupported, isAIStudyEnabled = true) {
    let isShowAITutorEnabled = false;

    if (showAITutor) {
      switch (isAITutorSupported) {
        case constants.LIVE:
          isShowAITutorEnabled = true;
          break;
        case constants.REVIEW:
          isShowAITutorEnabled = isAIReviewUser;
          break;
        default:
          isShowAITutorEnabled = false;
          break;
      }
    }

    return isAIStudyEnabled && isShowAITutorEnabled;
  }

  static isAHAITutorEnabled(enableAITutorOnAuthHome, showAITutor, isAIReviewUser, isAITutorSupported, isAIStudyEnabled = true) {
    return enableAITutorOnAuthHome
      && CommonUtils.isAITutorEnabled(showAITutor, isAIReviewUser, isAITutorSupported, isAIStudyEnabled);
  }

  /**
   * Returns marketing card data for hero
   *
   * @returns
   */
  static getChannelMarketingCardData = () => {
    const {
      NORMAL,
      EXTENDED
    } = constants.CHANNEL_MARKETING_IMAGE_TYPE;
    const language = Framework.getStoreRegistry().getStore('language');

    return [
      {
        image: constants.CHANNEL_MARKETING_CARD1,
        title: language.getMessage('hero.marketingcard1.copy.title'),
        description: language.getMessage('hero.marketingcard1.copy.description'),
        imageSize: NORMAL
      },
      {
        image: constants.CHANNEL_MARKETING_CARD2,
        title: language.getMessage('hero.marketingcard2.copy.title'),
        description: language.getMessage('hero.marketingcard2.copy.description'),
        imageSize: EXTENDED
      },
      {
        image: constants.CHANNEL_MARKETING_CARD3,
        title: language.getMessage('hero.marketingcard3.copy.title'),
        description: language.getMessage('hero.marketingcard3.copy.description'),
        imageSize: EXTENDED
      }
    ];
  };

  /**
   * Returns appropriate channelPersonalized, locationInAppForSol1D
   * for view channel card event for hero channel, herobook
   *
   * @param {*} mappedChannel
   * @returns
   */
  static getChannelsViewEventDataForSol1D = (mappedChannel) => {
    const heroBannerStore = Framework.getStoreRegistry().getStore('herobanner');
    const {
      bannerType
    } = getSnapshot(heroBannerStore);
    const {
      HERO_ETEXT_MAPPED_CHANNEL,
      HERO_RECENTCHANNEL
    } = constants.LOCATION_IN_APP;
    const {
      MAPPED_CHANNEL_ASSET_CARD,
      POPULAR_CHANNEL_ASSETS_CARD,
      LAST_ACCESSED_CHANNEL_ASSETS
    } = constants.MAPPED_CHANNEL_TEMPLATES;
    const {
      RECOMMENDED__ASSETS,
      POPULAR_ASSETS,
      RECENT_CHANNEL
    } = constants.CHANNEL_PERSONALIZED_VALUES;
    const mappedChannelTemplate = mappedChannel && mappedChannel.template;
    let channelPersonalized = null;
    let locationInApp = null;

    if (mappedChannelTemplate === MAPPED_CHANNEL_ASSET_CARD.TEXT) {
      channelPersonalized = RECOMMENDED__ASSETS;
      locationInApp = HERO_ETEXT_MAPPED_CHANNEL;
    } else if (mappedChannelTemplate === POPULAR_CHANNEL_ASSETS_CARD.TEXT) {
      channelPersonalized = POPULAR_ASSETS;
      locationInApp = HERO_ETEXT_MAPPED_CHANNEL;
    } else if (mappedChannelTemplate === LAST_ACCESSED_CHANNEL_ASSETS.TEXT) {
      channelPersonalized = RECENT_CHANNEL;
      locationInApp = HERO_RECENTCHANNEL;
    } else if (bannerType === constants.HERO_BANNER_TYPES.CHANNEL) {
      channelPersonalized = RECENT_CHANNEL;
      locationInApp = HERO_RECENTCHANNEL;
    }
    return {
      channelPersonalized,
      locationInAppForSol1D: locationInApp
    };
  };

  static waitForElm(selector) {
    // eslint-disable-next-line consistent-return
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector));
      }
      // eslint-disable-next-line no-undef
      const observer = new MutationObserver(() => {
        if (document.querySelector(selector)) {
          observer.disconnect();
          resolve(document.querySelector(selector));
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    });
  }

  /**
   * Function hanlde the text into clipboard
   * Note: Clipboard only works on https secure mode
   *
   * @param {*} text
   * @returns
   */
  static copyText = (text) => {
    if (!window?.navigator?.clipboard || !text) {
      return;
    }

    window.navigator.clipboard.writeText(text);
  };

  /**
   * Returns true if the book collection array contains archived
   *
   * @param {Array} collections
   * @returns
   */
  static isArchivedBook = (collections = []) => {
    const { BOOKSHELF_USER_COLLECTIONS } = constants;
    let isArchivedBook = false;

    if (collections.length > 0) {
      isArchivedBook = collections.some(
        collection => typeof collection === 'string' && collection.toLowerCase() === BOOKSHELF_USER_COLLECTIONS.ARCHIVED.toLowerCase()
      );
    }

    return isArchivedBook;
  };

  /**
   * Returns archived, unarchived books
   *
   * @param {<LIST>} books
   * @returns
   */
  static getArchivedOrUnArchivedBooks = (books = []) => {
    const archivedBooks = [];
    const unArchivedBooks = [];

    if (books.length > 0) {
      books.forEach((book) => {
        if (book.isArchived) archivedBooks.push(book);
        else unArchivedBooks.push(book);
      });
    }

    return {
      archivedBooks,
      unArchivedBooks
    };
  };

  /**
   * Returns true if user has books which are Archived
   *
   * @param {*} books
   * @returns
   */
  static hasArchivedBooks = (books = []) => books.filter(book => (book.isArchived))?.length > 0;

  /**
   * Tracks if a element is viewed / intersected in the viewport
   *
   */
  static appDomElementIntersectionTracker(properties = {}) {
    const elementsToTrackViewPortIntersection = constants.ELEMENTS_TO_TRACK_VIEWPORT_INTERSECTION;
    const intersectorCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          CommonUtils.logArchiveViewEvent(entry.target);
          CommonUtils.logNativeAppBannerViewEvent(entry.target, properties?.locationInApp);
          CommonUtils.logQRCodeViewEvent(entry.target, properties?.locationInApp);
        }
      });
    };

    if (!this.elementIntersectObserver) {
      const options = {
        rootMargin: '0px',
        threshold: 1.0
      };
      this.elementIntersectObserver = new window.IntersectionObserver(intersectorCallback, options);
    } else {
      this.elementIntersectObserver.disconnect();
    }

    if (elementsToTrackViewPortIntersection && elementsToTrackViewPortIntersection.length > 0) {
      elementsToTrackViewPortIntersection.forEach((element) => {
        const elementToTrack = document.querySelector(element);

        if (elementToTrack) {
          this.elementIntersectObserver.observe(elementToTrack);
        }
      });
    }
  }

  /**
   * Handles archive feature's view event
   *
   * @param {*} element
   */
  static logArchiveViewEvent(element) {
    const {
      VIEW_SELECT_TO_ARCHIVE_CTA,
      VIEW_SELECT_TO_UNARCHIVE_CTA,
      SELECT_TO_ARCHIVE,
      SELECT_TO_UNARCHIVE
    } = constants.ARCHIVE_EVENTS;
    const {
      SELECT_TO_ARCHIVE_OR_UNARCHIVE_BUTTONS,
      LOCATION_IN_APP,
      AUTHHOME_APPLICATION,
      EVENT_USER_INTERACTION
    } = constants;
    const elementId = element?.getAttribute('id');
    const isSelectToArchive = elementId === SELECT_TO_ARCHIVE_OR_UNARCHIVE_BUTTONS.SELECT_TO_ARCHIVE;
    const isSelectToUnarchive = elementId === SELECT_TO_ARCHIVE_OR_UNARCHIVE_BUTTONS.SELECT_TO_UNARCHIVE;
    const eventAction = isSelectToArchive ? VIEW_SELECT_TO_ARCHIVE_CTA : VIEW_SELECT_TO_UNARCHIVE_CTA;
    const eventLabel = isSelectToArchive ? SELECT_TO_ARCHIVE : SELECT_TO_UNARCHIVE;
    const customFields = {
      location_in_app: LOCATION_IN_APP.LIBRARYL2,
      is_ghost_account: CommonUtils.isGhostAccount(),
      event_value: null
    };

    if (isSelectToArchive || isSelectToUnarchive) {
      CommonUtils.dispatchGaEvent(
        AUTHHOME_APPLICATION,
        EVENT_USER_INTERACTION,
        eventAction,
        eventLabel,
        customFields
      );
    }
  }

  /**
   * Logs banner's view impression event
   *
   * @param {*} element
   */
  static logBannerViewImpression(element) {
    const containerId = element.target.getAttribute('id');
    const { BANNERS, AUTH_HOME_CATEGORY, EVENT_AUTHHOMEEVENT } = constants;

    if (containerId === BANNERS.CODE_REDEEMED.id) {
      CommonUtils.dispatchGaEvent(
        AUTH_HOME_CATEGORY,
        EVENT_AUTHHOMEEVENT,
        BANNERS.CODE_REDEEMED.view_event_action,
        BANNERS.CODE_REDEEMED.event_label,
        {
          is_ghost_account: CommonUtils.isGhostAccount()
        }
      );
    }
  }

  /**
   * To get channel reldirection learn url
   *
   * @param {*} ChannelAssetData
   * @param {*} mappedChannel
   * @param {*} channelData
   * @returns
   */
  static getChannelLearnUrl(ChannelAssetData, mappedChannel, channelData = {}, maxAssets = 4) {
    const {
      RECOMMENDED_ASSETS,
      JUMPBACK_IN_AND_RECOMMENDED_NEXT,
      POPULAR_TOPICS,
      START_WITH_TOPICS
    } = constants.CHANNEL_TYPES;
    const {
      recommendedAssets,
      popularTopics,
      startWithTopics,
      jumpBackIn,
      recommendedNext
    } = ChannelAssetData;
    const firstChannelCard = (mappedChannel && mappedChannel.cards && mappedChannel.cards.length > 0 && mappedChannel.cards[0]);
    const { type, cardIndex } = (firstChannelCard && firstChannelCard.callForAction) || {};
    let redirectionUrl = null;

    switch (type) {
      case RECOMMENDED_ASSETS: {
        if (recommendedAssets && recommendedAssets[cardIndex]) {
          const { url } = recommendedAssets[cardIndex];
          redirectionUrl = env.STUDY_CHANNEL_URL + url;
        }
        break;
      }
      case POPULAR_TOPICS: {
        if (popularTopics && popularTopics[cardIndex]) {
          const { url } = popularTopics[cardIndex];
          redirectionUrl = env.STUDY_CHANNEL_URL + url;
        }
        break;
      }
      case START_WITH_TOPICS: {
        if (startWithTopics && startWithTopics[cardIndex]) {
          const { url } = startWithTopics[cardIndex];
          redirectionUrl = env.STUDY_CHANNEL_URL + url;
        }
        break;
      }
      case JUMPBACK_IN_AND_RECOMMENDED_NEXT: {
        if (jumpBackIn || recommendedNext) {
          const hasRecommendedNext = recommendedNext && recommendedNext.length > 0;
          const jumpBackInAndUpNext = [...jumpBackIn, ...(hasRecommendedNext ? recommendedNext : [])].slice(
            0, maxAssets
          );
          if (jumpBackInAndUpNext[cardIndex]) {
            const { url } = jumpBackInAndUpNext[cardIndex];
            redirectionUrl = env.STUDY_CHANNEL_URL + url;
          }
        }
        break;
      }
      default:
        break;
    }

    return redirectionUrl || channelData.learnUrl || channelData.url;
  }

  /*
   * Handles native app banner feature's view event
   *
   * @param {*} element
   * @param {*} locationInApp
   */
  static logNativeAppBannerViewEvent(element, locationInApp = constants.LOCATION_IN_APP.AUTHHOME) {
    const { NATIVE_APP_BANNER_CLASS } = constants;
    const {
      EVENT_NAME,
      VIEW_EVENT_ACTION,
      VIEW_EVENT_LABEL
    } = constants.NATIVE_APP_BANNER_EVENTS;

    if (CommonUtils.viewEventTracker.has(VIEW_EVENT_ACTION)
      && CommonUtils.viewEventTracker.get(VIEW_EVENT_ACTION)?.isTriggered) {
      return;
    }

    const isDrawer = element?.classList?.contains(NATIVE_APP_BANNER_CLASS.DRAWER);
    const isBanner = element?.classList?.contains(NATIVE_APP_BANNER_CLASS.BANNER);
    let testVariantsEventLabel = null;

    switch (element.getAttribute('data-componentId')) {
      case constants.COMPONENT_IDS.BOTTOM_SHEET:
        testVariantsEventLabel = constants.NATIVE_APP_PROMO_TEST_EVENT_LABEL[constants.NATIVE_APP_PROMO_TYPES.DRAWER];
        break;
      case constants.COMPONENT_IDS.BOTTOM_BANNER:
        testVariantsEventLabel = constants.NATIVE_APP_PROMO_TEST_EVENT_LABEL[constants.NATIVE_APP_PROMO_TYPES.BANNER];
        break;
      case constants.COMPONENT_IDS.MODAL:
        testVariantsEventLabel = constants.NATIVE_APP_PROMO_TEST_EVENT_LABEL[constants.NATIVE_APP_PROMO_TYPES.MODAL];
        break;
      default:
        break;
    }

    const eventLabel = isDrawer ? VIEW_EVENT_LABEL.BOTTOM_DRAWER : VIEW_EVENT_LABEL.BANNER;

    if (isDrawer || isBanner || testVariantsEventLabel) {
      // save to local view event action tracker
      CommonUtils.viewEventTracker.set(VIEW_EVENT_ACTION, {
        isTriggered: true
      });

      CommonUtils.dispatchGaEvent(
        constants.AUTHHOME_APPLICATION,
        EVENT_NAME,
        VIEW_EVENT_ACTION,
        testVariantsEventLabel || eventLabel,
        {
          app_activity_area_code: locationInApp,
          is_ghost_account: CommonUtils.isGhostAccount()
        }
      );
    }
  }

  static logQRCodeViewEvent = (element, locationInApp) => {
    if (!element || locationInApp !== constants.LOCATION_IN_APP.AUTHHOME) {
      return;
    }

    const componentAttribute = element.getAttribute('data-componentId');
    const isQRCodeButton = CommonUtils.localStringCompare(
      componentAttribute,
      constants.QR_COMPONENT_IDS.QR_CODE_BUTTON
    );
    const isQRCodeIcon = CommonUtils.localStringCompare(
      componentAttribute,
      constants.QR_COMPONENT_IDS.QR_CODE_ICON
    );

    if (isQRCodeButton || isQRCodeIcon) {
      CommonUtils.dispatchQRcodeViewEvent(
        constants.QR_CODE_VIEW_EVENT.EVENT_LABEL_CONTINUE_IN_THE_APP,
        constants.QR_CODE_VIEW_EVENT.EVENT_LABEL_CONTINUE_IN_THE_APP
      );
    }
  };

  static logQRCodeTestVariantViewEvent = (element) => {
    if (!element) {
      return;
    }

    const hasTestVariants = constants.QR_CODE_CARD_COMPONENT_IDS.includes(
      element.getAttribute('data-componentid')
    );

    if (!hasTestVariants) {
      return;
    }

    const variant = element.getAttribute('data-componentid').split(constants.QR_CODE_CARD)?.at(1);
    const testVariantsEventLabel = constants.QR_CODE_TEST_VARIANT_EVENT_LABEL[variant];

    if (testVariantsEventLabel) {
      CommonUtils.dispatchQRcodeViewEvent(
        constants.QR_CODE_VIEW_EVENT.EVENT_LABEL_CARD,
        testVariantsEventLabel
      );
    }
  };

  /**
   * To get PopularChannels apart from light weight channel
   *
   * @param {*} channelCards
   * @returns
   */
  static getPopularChannels = channelCards => channelCards.filter(
    ({ type }) => !this.localStringCompare(type, constants.LIGHTWEIGHT_CHANNEL_TYPE)
  );

  /**
   * To get active channel card
   * @param {*} channelCards
   * @returns
   */

  static getActiveChannelCards = (channelCards, includeMostRelated = false) => {
    let activeChannelCards = [];
    if (channelCards && channelCards.length > 0) {
      const popularChannel = this.getPopularChannels(channelCards);
      activeChannelCards = popularChannel.filter(
        channel => (includeMostRelated
          ? channel.active || channel.isMostRelated
          : channel.active)
      );
    }

    return activeChannelCards;
  };

  /**
   * Returns true if user is not ghost account, the book has courseId and bmc is belonged to MLM
   *
   * @param {String} courseId
   * @param {String} bmc
   * @returns {Boolean}
   */
  static isMyLabSubscription = (
    courseId = null,
    BMC
  ) => !!(!CommonUtils.isGhostAccount() && CommonUtils.isIntegratedBMC(BMC) && courseId);

  /**
   * Fetches and returns piSession refresh token from local storage
   *
   * @returns {String}
   */
  static getPiRefreshToken = () => {
    let currentRefreshToken = '';
    const { LOCAL_STORAGE_KEYS } = constants;
    const localStorageDataList = Object.entries(window?.localStorage || []);

    if (localStorageDataList.length > 0) {
      const currentData = localStorageDataList.find(item => item[0]?.endsWith(LOCAL_STORAGE_KEYS.REFRESH_TOKEN));

      if (currentData[1]) currentRefreshToken = currentData[1];
    }

    return currentRefreshToken;
  };

  /**
   * Determine wheather user have bundle or subscribed  or Non-subscribed
   *
   * @returns
   */

  static channelsSubStatus = () => {
    const user = getSnapshot(Framework.getStoreRegistry().getStore('user'));
    const channelsSubscriber = CommonUtils.isChannelsSubscriber();
    if (CommonUtils.isBundleSubscriber()) {
      return constants.SUBSCRIBER_STATUS.BUNDLE;
    } if (channelsSubscriber) {
      return constants.SUBSCRIBER_STATUS.SUBSCRIBER;
    }
    return constants.SUBSCRIBER_STATUS.NON_SUBSCRIBER;
  };

  /**
   * Determine wheather bundle subscribed user or not
   *
   * @returns
   */
  static isBundleSubscriber = () => {
    const user = getSnapshot(Framework.getStoreRegistry().getStore('user'));
    return CommonUtils.hasSubscription(user, [constants.ACTIVE], [constants.BUNDLE]);
  };

  /**
   * Determine wheather Channels Subscriber subscribed user or not
   *
   * @returns
   */
  static isChannelsSubscriber = () => {
    const user = getSnapshot(Framework.getStoreRegistry().getStore('user'));
    return this.isBundleSubscriber()
      || CommonUtils.hasSubscription(user, [constants.ACTIVE], [constants.ENTITLEMENTLEVEL_STUDY])
      || user?.hasChannelsAccess;
  };

  /**
   * Whether Channels remediation experiences should render (subscriber with a hero book channel id and non-empty assets).
   *
   * @param {Array} [assets] - The assets to check if they are non-empty.
   * @returns {boolean}
   */
  static shouldShowChannelsRemediation = (assets) => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');

    const heroBook = herobanner.getHeroBook();
    const { hasAccessToExactOrLiteMatch } = heroBook || {};
    const isChannelsSubscriberWithChannelId = Boolean(hasAccessToExactOrLiteMatch) && !!herobanner?.hasChannelId();

    return assets
      ? isChannelsSubscriberWithChannelId && Array.isArray(assets) && assets.length > 0
      : isChannelsSubscriberWithChannelId;
  };

  /**
   * Current screen event label
   *
   * @returns
   */
  static getCurrentScreenEventLabel = (flags = {}) => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const courseCollection = Framework.getStoreRegistry().getStore('coursecollection');
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const { channelMappingType } = courseCollection.getActiveCourseCollection() || {};
    const isRelatedMapping = CommonUtils.localStringCompare(
      channelMappingType || '', constants.CHANNEL_MAPPING_TYPES.RELATED
    );
    const isExactLiteMapping = flags.enableExactLiteMapping && CommonUtils.localStringCompare(
      channelMappingType || '', constants.CHANNEL_MAPPING_TYPES.EXACTLITE
    );
    let template = herobanner.bannerType;
    const { isAETEnabled } = herobanner;
    const heroBook = herobanner.getHeroBook();
    const isStudent = CommonUtils.getIsStudent(heroBook);

    if (isAETEnabled) {
      if (isStudent) {
        template = constants.AET_TEMPLATE_LABEL.STUDENT_AET;
      } else {
        const hasAssignments = assignmentStore.assignments.length > 0;

        template = hasAssignments
          ? constants.AET_TEMPLATE_LABEL.EDUCATOR_AET
          : constants.AET_TEMPLATE_LABEL.EDUCATOR_AET_EMPTY_STATE;
      }
    } else if (isExactLiteMapping) {
      template = constants.ETEXT_MAPPEDCHANNEL_LITE;
    } else if (isRelatedMapping) {
      template = constants.ETEXT_RELATED_CHANNEL;
    }

    return constants.TEMPLATE_LABEL[template] || null;
  };

  static isAbsoluteURL = (url) => {
    const regex = new RegExp('^(?:[a-z+]+:)?//', 'i');

    return regex.test(url);
  };

  static getExamPrepUrl = (url = '') => `${url}/${constants.CHANNEL_HYPER_LINKS.EXAM_PREP}`;

  static getChannelLink = (channelId, linkName = '') => `${env.STUDY_CHANNEL_URL}/${channelId}${linkName ? `/${linkName}` : ''}`;

  static getAssetParamsData(params = {}) {
    return { ...(params || {}) };
  }

  static constructAssetData(response = {}) {
    const {
      // eslint-disable-next-line camelcase
      content_type = null,
      type,
      channelsUrl,
      locationData,
      params,
      completed = false
    } = response || {};
    const {
      chapterTitle = null,
      topicTitle = null
    } = locationData;
    const {
      title,
      duration = null,
      thumbUrl = null,
      question
    } = CommonUtils.getAssetParamsData(params) || {};
    let renderTitle = title;

    if (!CommonUtils.isEmpty(question || {})) {
      const {
        rtext
      } = CommonUtils.getAssetParamsData(question && question.params) || {};
      renderTitle = rtext;
    }

    return {
      assetContentType: content_type,
      assetDuration: duration,
      assetTitle: renderTitle,
      assetType: type,
      chapterTitle,
      thumbUrl,
      topicTitle,
      url: channelsUrl,
      completed
    };
  }

  static filterAssets = (recommendedAssets) => {
    let assets = [];

    if (recommendedAssets && recommendedAssets.length > 0) {
      assets = recommendedAssets.filter(asset => (
        asset !== null
        && asset?.assetType !== constants.ASSET_CONTENT_TYPE.TEXT_BLOCK
        && asset?.assetType !== constants.ASSET_CONTENT_TYPE.IMAGE
        && asset?.assetType !== constants.ASSET_CONTENT_TYPE.AUDIO
      ));
    }

    return assets;
  };

  /**
   * To checkAvailabilityOfBMC to give Array
   * return Boolean
   */
  static checkAvailabilityOfBMC = (bmc, books) => {
    if (Array.isArray(bmc)) {
      return books.some(book => (
        bmc.includes(book?.product_entitlements?.business_model_code)
        && book?.product_entitlements?.expired === false
      ));
    }

    return books.some(book => book?.product_entitlements?.business_model_code === bmc && book?.product_entitlements?.expired === false);
  };

  /**
   * To check user if he is a EHI user
   * return Boolean
   */
  static isHEIUser() {
    if (this.isHEIUserStatus) {
      return this.isHEIUserStatus;
    }
    const userStore = Framework.getStoreRegistry().getStore('user');
    const user = getSnapshot(userStore);
    const bookshelf = Framework.getStoreRegistry().getStore('bookshelf');
    const books = getSnapshot(bookshelf).books;
    // First priority to check on marin user profile
    if (user.gpsSubscriptions) {
      this.isHEIUserStatus = !!user.isHEI;
    }
    // Second priority to check on marin book BMC
    if (!this.isHEIUserStatus && books && books.length > 0) {
      this.isHEIUserStatus = this.checkAvailabilityOfBMC(constants.HEI_BMC_CODES_LIST, books);
    }

    return this.isHEIUserStatus;
  }

  /**
   * call generateUId to get uId and jwtToken
   * @param {*} locationInApp
   * @param {*} calledFrom
   */
  static fetchUIDQRcode = async (locationInApp, shouldOpenModal = true) => {
    // eslint-disable-next-line no-undef
    if (this.controller) {
      // do not keep duplicate connections alive
      this.controller.abort();
    }
    this.controller = new AbortController();
    const user = getSnapshot(Framework.getStoreRegistry().getStore('user'));
    const token = user.token;
    const error = { isError: true };
    const { QR_CODE_HTTP_REQUEST_METHODS, QR_CODE_EVENT } = constants;
    fetchEventSource(`${env.MARIN_API_BASE_URL}/qr/generateUId`, {
      method: QR_CODE_HTTP_REQUEST_METHODS.POST,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        refreshToken: CommonUtils.getPiRefreshToken()
      }),
      signal: this.controller.signal,
      onmessage(msg) {
        if (msg.event === 'FatalError') {
          Framework.getEventManager().publish(constants.QR_CODE_SERVICE_FETCHED, { ...error, shouldOpenModal });
          CommonUtils.logQRCodeEvent(locationInApp, QR_CODE_EVENT.QR_CODE_API_FAILURE_EVENT_LABEL);
          console.log('onmessage error');
        }
        const { uId, status } = JSON.parse(msg.data);
        if (uId) {
          Framework.getEventManager().publish(constants.QR_CODE_SERVICE_FETCHED, { ...JSON.parse(msg.data), shouldOpenModal });
        }
        if (status) {
          if (status.status && typeof status.status === 'string' && (status.status.toLowerCase() === constants.QR_CODE_STATUS.DONE || status.status.toLowerCase() === constants.QR_CODE_STATUS.EXPIRED)) {
            Framework.getEventManager().publish(constants.QR_CODE_SERVICE_FETCHED, { closeConnection: true, shouldOpenModal });
          }
        }
      },
      onerror(err) {
        if (err) {
          Framework.getEventManager().publish(constants.QR_CODE_SERVICE_FETCHED, { ...error, shouldOpenModal });
          CommonUtils.logQRCodeEvent(locationInApp, QR_CODE_EVENT.QR_CODE_API_FAILURE_EVENT_LABEL);
          throw err;
        }
      }
    });
  };

  /**
  * return menu array based on requirement
  * @returns
  */
  static getHeaderTabsMenuListItems(
    books,
    flags,
    isBookshelfFetched,
    isIntegratedUI = false,
    isHome2 = false
  ) {
    const isHEIUser = this.isHEIUser();
    const isHEILDShowContent = isHEIUser && !flags.showHEIUserContent;
    const lang = Framework.getStoreRegistry().getStore('language');
    const headerL2TabsMenuList = [];
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook() || {};
    const isStudent = CommonUtils.getIsStudent(heroBook);
    const isInstructor = !isStudent;
    const isAetEnabled = getSnapshot(herobanner)?.isAETEnabled;
    const isNonMlmInstructorDeeplinkLaunch = CommonUtils.isNonMlmInstructorDeeplinkLaunch();
    const userStore = Framework.getStoreRegistry().getStore('user');
    const user = getSnapshot(userStore);
    const isInternalUser = CommonUtils.isInternalUser(user);
    const hideMoreAtPearson = !isInternalUser
    && (isHome2 && isInstructor && (isAetEnabled || isNonMlmInstructorDeeplinkLaunch));

    if (isHEILDShowContent) {
      headerL2TabsMenuList.push({
        id: constants.HEI_LEARN_LANGUAGE,
        name: lang.getMessage('header.menu.learnLanguage')
      });
    }

    if (!isHEIUser && isBookshelfFetched && !isIntegratedUI && !hideMoreAtPearson) {
      headerL2TabsMenuList.push({
        id: constants.MORE_AT_PEARSON,
        name: lang.getMessage('header.menu.moreAtPearson')
      });
    }

    if (CommonUtils.showMyEtextbookLinkInHeader(books)) {
      headerL2TabsMenuList.push({
        id: 'MY_ETEXTBOOK',
        name: lang.getMessage('hero.myETextbooks')
      });
    }

    return headerL2TabsMenuList;
  }

  /**
   * return modal object to populate the modal
   *
   *
   * @returns
   */
  static getModalProps() {
    const language = Framework.getStoreRegistry().getStore('language');
    const modalProps = {
      title: language.getMessage('header.continueInTheApp'),
      header: language.getMessage('header.continueInTheApp'),
      subHeader: language.getMessage('header.qrCode.subHeaderTxt'),
      ignoreAppQrCode: language.getMessage('header.qrCode.dontHaveApp'),
      footer: language.getMessage('header.qrCode.footerTxt'),
      close: language.getMessage('onboard.close')
    };
    return modalProps;
  }

  static getMLMRouteBookData = (path) => {
    const decodedPath = CommonUtils.getMLMPath(path);

    if (!decodedPath) return null;

    // regex updated to handle separately /courses/:courseId and also /courses/:courseId/products/:productId and /products/:productId
    const pathMatchRegex = /^(?:\/?courses\/([^/]+)(?:\/products\/([^/]+))?|\/?products\/([^/]+))$/;

    const pathMatch = decodedPath.match(pathMatchRegex);
    const courseId = (pathMatch && pathMatch[1]) || null;
    const productId = (pathMatch && (pathMatch[2] || pathMatch[3])) || null;

    return {
      courseId,
      bookId: productId
    };
  };

  /**
   * Add UTM Parameter and amend Url hash at end of the Url incase
   *
  */
  static getUrlWithUTMParams = (url, utmParams) => {
    let locationUrl = url;
    if (url && !url.includes(utmParams)) {
      const hashReg = new RegExp('#.+=([^&|/|?]+)', 'i');
      const hashMatch = url.match(hashReg);
      let urlHash = '';
      if (hashMatch) {
        urlHash = hashMatch[0];
        locationUrl = locationUrl.replace(urlHash, '');
      }
      locationUrl = PathUtils.addQueryParameter(utmParams, locationUrl) + urlHash;
    }

    return locationUrl;
  };

  /* method to log QR code event
   *
   * @param {*} eventLabel
   * @param {*} bookData
   */
  static logQRCodeEvent(locationInApp, eventLabel) {
    const { AUTHHOME_APPLICATION, EVENT_USER_INTERACTION, QR_CODE_EVENT } = constants;
    const customFields = {
      location_in_app: locationInApp,
      is_ghost_account: CommonUtils.isGhostAccount()
    };
    CommonUtils.dispatchGaEvent(AUTHHOME_APPLICATION, EVENT_USER_INTERACTION, QR_CODE_EVENT.CONTINUE_IN_APP_CLICK_EVENT_ACTION, eventLabel, customFields);
  }

  static getMLMPath = (path) => {
    const {
      mlmcontextencoded
    } = path?.params || {};

    try {
      // eslint-disable-next-line no-undef
      return atob(mlmcontextencoded);
    } catch (err) {
      return null;
    }
  };

  static getBase64Decoded = (encodedValue) => {
    try {
      // eslint-disable-next-line no-undef
      return atob(encodedValue);
    } catch (err) {
      return null;
    }
  };

  static getBase64Encoded = (encodedValue) => {
    try {
      // eslint-disable-next-line no-undef
      return btoa(encodedValue);
    } catch (err) {
      return null;
    }
  };

  /**
   * byLastAccessedTime === true (Returns last accessed books)
   * byLastAccessedTime === false (Returns newly added books)
   *
   * @param {Array} books
   * @param {Boolean} byLastAccessedTime
   * @returns
   */
  static getConditionalActiveBooks = (books, byLastAccessedTime) => {
    const filterBooks = books.filter(book => (
      (byLastAccessedTime ? book.last_accessed_time : !book.last_accessed_time)
      && CommonUtils.isActiveBook(book)
      && !book.isArchived));
    if (byLastAccessedTime) {
      filterBooks.sort((a, b) => b.last_accessed_time - a.last_accessed_time);
    }

    if (!byLastAccessedTime && filterBooks.length > 1) {
      filterBooks.sort((a, b) => {
        const bookATimestamp = new Date(a?.product_entitlements?.start_date || null)?.getTime();
        const bookBTimestamp = new Date(b?.product_entitlements?.start_date || null)?.getTime();

        return (bookBTimestamp - bookATimestamp);
      });
    }

    return { firstBook: filterBooks[0] || null, books: filterBooks };
  };

  /**
   * Sorts and returns collections based on createdDate property
   *
   * @param {Array} userCollections
   * @returns
   */
  static getRecentlyAddedCourses = (userCollections) => {
    if (userCollections.length <= 1) return userCollections;

    const collections = [...userCollections];
    collections.sort((a, b) => b.createdDate - a.createdDate);

    return collections;
  };

  /**
   * Returns last accessed book's course collection index
   *
   * @param {Array} courses
   */
  static getLastAccessedBookIdx = (courses = [], userCollections = []) => {
    const findPriorityFlow = [];
    let priorityFlow = null;
    let foundBook = { book_id: null, course_id: null, product_id: null };
    const bookshelf = Framework.getStoreRegistry().getStore('bookshelf');
    const { books } = getSnapshot(bookshelf);
    const lastAccessedBooks = CommonUtils.getConditionalActiveBooks(books, true) || {};
    const newAdded = CommonUtils.getConditionalActiveBooks(books, false) || {};
    const recentlyAddedCourse = CommonUtils.getRecentlyAddedCourses(userCollections)[0];
    const {
      last_accessed_time: lastAccessedBooktime = null
    } = lastAccessedBooks.firstBook || {};

    const {
      product_entitlements: newlyAddedBookEntitlements = null
    } = newAdded.firstBook || {};

    const {
      createdDate: recentlyAddedCourseTime = null
    } = recentlyAddedCourse || {};

    if (lastAccessedBooktime) {
      findPriorityFlow.push({
        name: 'lastAccessedBook', timeStamp: lastAccessedBooktime, value: lastAccessedBooks.firstBook
      });
    }
    if (newlyAddedBookEntitlements && newlyAddedBookEntitlements.start_date) {
      const newlyAddedBookTime = new Date(newlyAddedBookEntitlements.start_date).getTime();
      findPriorityFlow.push({ name: 'newlyAddedBook', timeStamp: newlyAddedBookTime, value: newAdded.firstBook });
    }
    if (recentlyAddedCourseTime) {
      findPriorityFlow.push({
        name: 'recentlyAddedCourse', timeStamp: recentlyAddedCourseTime, value: recentlyAddedCourse?.name || null
      });
    }

    if (findPriorityFlow.length > 0) {
      // Sorting out the timestamp to findout which flow like recent course access or recently access book or newly added book
      findPriorityFlow.sort((a, b) => b.timeStamp - a.timeStamp);
      priorityFlow = findPriorityFlow[0];
      if (priorityFlow.name === 'lastAccessedBook' || priorityFlow.name === 'newlyAddedBook') {
        foundBook = priorityFlow.value;
      }
    }

    return courses.findIndex((course) => {
      CommonUtils.moveBookToFirstPosition(course, {
        bookId: foundBook.book_id,
        courseId: foundBook.course_id,
        productId: foundBook.product_id
      });
      const {
        bookId = null,
        courseId = null,
        productId = null
      } = (course && course.collection && course.collection[0]) || {};

      if (priorityFlow?.name === 'recentlyAddedCourse') {
        return priorityFlow?.value === course?.id;
      }

      return (
        foundBook.book_id === bookId
        && foundBook.course_id === courseId
        && foundBook.product_id === productId
      );
    });
  };

  /**
  *
  * Return the CTS based one channel mapping
  * @param {*} channelCards
  * @param {*} isBundleUser
  * @returns
  */
  static getChannelContainerCopytext(channelCards = [], isBundleUser) {
    const language = Framework.getStoreRegistry().getStore('language');
    let returnText = 'channel.genericBundle';
    if (channelCards && channelCards.some(ele => ele.recommended)) {
      returnText = 'channel.relatedBundle';
    }

    // eslint-disable-next-line no-nested-ternary
    return language.getMessage(isBundleUser ? returnText : 'channel.noBundletitle');
  }

  /**
   *
   * Returns true if it is algoliaSearch else return false
   * @param {Object} userOrProductSubscription
   * @returns
   */
  static isAlgoliaSearch(user, books = []) {
    const hasActiveBooks = CommonUtils.hasActiveBooks(books);
    const isPplusUser = CommonUtils.isPPlusUser(user, constants.AUTH_HOME);
    const bookLengthCheck = books?.length > 0;
    return !this.isHEIUser() && (!bookLengthCheck || !hasActiveBooks || isPplusUser);
  }

  /**
   *
   * Returns current value based on the channel mapping type
   * @param {*} heroBook
   * @returns
   */
  static containerValue(heroBook = {}, flags = {}) {
    let template = constants.TEMPLATE_LABEL.BOOK;
    const isExactMapping = CommonUtils.localStringCompare(
      heroBook.channel_mapping_type, constants.CHANNEL_MAPPING_TYPES.EXACT
    );
    const isExactLiteMapping = flags?.enableExactLiteMapping && CommonUtils.localStringCompare(
      heroBook.channel_mapping_type, constants.CHANNEL_MAPPING_TYPES.EXACTLITE
    );
    if (heroBook.channel_id
      && isExactMapping
    ) {
      template = constants.TEMPLATE_LABEL.BOOK_WITH_CHANNEL;
    }
    if (heroBook.channel_id
      && isExactLiteMapping
    ) {
      template = constants.TEMPLATE_LABEL.ETEXT_MAPPEDCHANNEL_LITE;
    }

    return template;
  }

  /**
   * Function to push telementry/GA event
   * @param {string} event_type
   * @param {object} additionalFields or extraFields
   */
  static dispatchAIGAEvent(eventType, additionalFields = null) {
    const gaEventData = GAConstants.GAEvents[eventType];
    const timeStamp = {
      transaction_local_dt: this.getLocalIsoTime(),
      user_local_event_dt: this.getLocalIsoTime(),
      event_unix_timestamp: new Date().getTime()
    };
    const eventContent = {
      ...gaEventData,
      ...additionalFields,
      ...timeStamp
    };

    if (gaEventData) EventProvider.getEventManager().dispatch(eventContent);
  }

  /**
   * Returns channel added source
   *
   * @param {*} channelCard
   * @returns
   */
  static getChannelAddedSource = (channelCard) => {
    let source = constants.LOCATION_IN_APP.NEW_OTHER_COURSE_CONTAINER;
    const courseCollection = Framework.getStoreRegistry().getStore('coursecollection');
    const {
      channelMappingType
    } = courseCollection.getActiveCourseCollection() || {};
    const isRelatedMapping = CommonUtils.localStringCompare(
      channelMappingType, constants.CHANNEL_MAPPING_TYPES.RELATED
    );

    const {
      isMostRelated,
      recommended
    } = channelCard || {};

    if (isMostRelated) source = constants.LOCATION_IN_APP.NEW_MOST_RELATED_CONTAINER;
    else if (recommended) source = constants.LOCATION_IN_APP.NEW_OTHER_COURSE_CONTAINER_RELATED;
    else if (isRelatedMapping) source = constants.LOCATION_IN_APP.NEW_OTHER_COURSE_CONTAINER_UNRELATED;

    return source;
  };

  /**
   * Returns current hero template
   *
   * @param {*} bannerType
   * @returns
   */
  static getCurrentHeroTemplate = (bannerType) => {
    const courseCollection = Framework.getStoreRegistry().getStore('coursecollection');
    const {
      channelMappingType
    } = courseCollection.getActiveCourseCollection() || {};
    const isRelatedMapping = CommonUtils.localStringCompare(
      channelMappingType, constants.CHANNEL_MAPPING_TYPES.RELATED
    );

    if (isRelatedMapping && bannerType === constants.HERO_BANNER_TYPES.BOOK) {
      return constants.TEMPLATE_LABEL.ETEXT_RELATED_CHANNEL;
    }

    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook();
    const isAETEnabled = herobanner.isAETEnabled;
    const isStudent = CommonUtils.getIsStudent(heroBook);

    // If AET is enabled, return the appropriate template based on the user role
    if (isAETEnabled) {
      if (isStudent) {
        return constants.AET_TEMPLATE_LABEL.STUDENT_AET;
      }

      const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
      const hasAssignments = assignmentStore.assignments.length > 0;

      if (hasAssignments) {
        return constants.AET_TEMPLATE_LABEL.EDUCATOR_AET_ASSIGNMENTS;
      }

      return constants.AET_TEMPLATE_LABEL.EDUCATOR_AET_EMPTY_STATE;
    }

    return bannerType;
  };

  static secondsToTime(secs) {
    const hours = Math.floor(secs / (60 * 60));

    const divisorForMinutes = secs % (60 * 60);
    const minutes = Math.floor(divisorForMinutes / 60);

    const divisorForSeconds = divisorForMinutes % 60;
    const seconds = Math.ceil(divisorForSeconds);

    return {
      h: hours,
      m: minutes,
      s: seconds
    };
  }

  /**
   * Returns true if the book is in the list
   *
   * @param {Object} bookJson
   * @param {Object} extraConfig
   * @returns
   */
  static isTitleInObjectList = (bookJson = {}, extraConfig = {}) => {
    const { bookId, productId } = extraConfig;
    const allowAllBooks = bookJson?.titles?.length === 0;
    const exclude = bookJson?.exclude || false;

    return allowAllBooks || CommonUtils.isTitleInList(
      bookJson?.titles, { bookId, productId }, exclude
    );
  };

  /**
   * Common config for AI in both AH and Ereader
   *
   * @param {*} flags
   * @param {*} bookData
   * @param {*} extraConfig
   * @returns
   */
  static commonAIConfig = (flags, bookData, extraConfig = {}) => {
    const config = {};
    const {
      enableNewMathConfig,
      aiStudyToolNameABTest,
      AIEnableNewValidateAnswer,
      processMarkDownFirst,
      shareChatbotReference,
      AIEnableHigherOrderSuggestion,
      AIEnableQuizRandomization,
      quizQuestionLimit,
      enableAgenticAI,
      agenticGraphConfig,
      agenticGraphConfigForDynamicSummary,
      AIEnableQuizOptionShuffle,
      AIEnableDynamicSummary,
      AIEnableDynamicQuiz,
      AIShowFooterFAQLink
    } = flags;
    const { isAITutorEnabled, bookId, productId } = extraConfig;
    const enableProcessMarkDownFirst = this.isTitleInList(
      processMarkDownFirst?.titles, { bookId, productId }
    );
    config.enableNewMathConfig = enableNewMathConfig && isAITutorEnabled && bookData.isAITutorSupported === constants.REVIEW;
    config.AIEnableDynamicSummary = isAITutorEnabled && Boolean(bookData.isDynamicSummarySupported && AIEnableDynamicSummary);
    config.AIEnableDynamicQuiz = isAITutorEnabled && Boolean(bookData.isDynamicQuizSupported && AIEnableDynamicQuiz);
    config.agenticGraphConfig = config.AIEnableDynamicSummary ? agenticGraphConfigForDynamicSummary : agenticGraphConfig;


    return {
      aiStudyToolNameABTest,
      AIEnableNewValidateAnswer,
      processMarkDownFirst: enableProcessMarkDownFirst,
      shareChatbotReference,
      AIEnableHigherOrderSuggestion,
      AIEnableQuizRandomization,
      quizQuestionLimit,
      enableAgenticAI,
      agenticGraphConfig,
      AIEnableQuizOptionShuffle,
      AIShowFooterFAQLink,
      ...config
    };
  };

  /**
   * Returns true if current book is inside the books array
   *
   * @param {Array} books
   * @param {object} currentBook
   * @param {boolean} exclude
   * @returns
   */
  static isTitleInList = (books = [], currentBook = {}, exclude = false) => {
    const titleExists = books.some(
      book => book?.bookId === currentBook?.bookId || book?.productId === currentBook?.productId
    );

    if (exclude) {
      return !titleExists;
    }

    return titleExists;
  };

  /**
   * Returns the title if current book is inside the books array
   *
   * @param {Array} books
   * @param {object} currentBook
   * @returns
   */
  static getTitleInList = (books = [], currentBook = {}) => books.find(
    book => book?.bookId === currentBook?.bookId || book?.productId === currentBook?.productId
  ) || {};

  /**
   * Returns true if any channel card is in active ie addable status
   *
   */
  static hasAddableChannels = () => {
    const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
    const popularChannelCards = this.getPopularChannels(heroBanner.getAllPopularChannels());
    let hasAddableChannels = false;

    if (popularChannelCards && popularChannelCards.length > 0) {
      hasAddableChannels = popularChannelCards.some(card => card.active);
    }

    return hasAddableChannels;
  };

  /**
   * To get Channel mapping type label
   *
   * @param {*} channelMappingType
   * @returns
   */
  static getChannelProductMappingType = (channelMappingType) => {
    const channelProductMappingType = {
      [constants.CHANNEL_MAPPING_TYPES.EXACT]: constants.ETEXT_CHANNEL_MAPPING_TYPE_LABEL.ETEXT_CHANNELS_EXACT,
      [constants.CHANNEL_MAPPING_TYPES.EXACTLITE]: constants.ETEXT_CHANNEL_MAPPING_TYPE_LABEL.ETEXT_CHANNELS_EXACTLITE,
      [constants.CHANNEL_MAPPING_TYPES.RELATED]: constants.ETEXT_CHANNEL_MAPPING_TYPE_LABEL.ETEXT_CHANNELS_RELATED,
      [constants.CHANNEL_MAPPING_TYPES.GENERIC]: constants.ETEXT_CHANNEL_MAPPING_TYPE_LABEL.ETEXT_CHANNELS_NOTMAPPED
    };
    const renderChannelMappingType = channelMappingType
    && typeof channelMappingType === 'string' && channelMappingType.toLocaleUpperCase();

    return channelProductMappingType[renderChannelMappingType]
    || channelProductMappingType[constants.CHANNEL_MAPPING_TYPES.GENERIC];
  };

  /**
   * Show my etextbook link in header
   *
   * @param {Array} books
   */
  static showMyEtextbookLinkInHeader = (books = []) => {
    const activeBooks = CommonUtils.getActiveBooks(books);
    const { unArchivedBooks } = CommonUtils.getArchivedOrUnArchivedBooks(activeBooks);
    let showMyEtextbookLinkInHeader = false;

    if (unArchivedBooks?.length > 1) {
      // Has more than one unarchived books
      showMyEtextbookLinkInHeader = true;
    } else if (unArchivedBooks?.length === 1) {
      // Has one active unarchived book
      showMyEtextbookLinkInHeader = books?.length > 1;
    } else if (books?.length > 0) {
      // Has expired (or) paused books
      showMyEtextbookLinkInHeader = true;
    }

    return showMyEtextbookLinkInHeader;
  };

  /**
   * Returns all channelIds of a book
   *
   * @param {String} channelId
   * @param {Array} secondaryChannels
   * @param {String} channelMappingType
   * @returns
   */
  static getAllChannelIds(channelId, secondaryChannels = [], channelMappingType) {
    if (!channelId) return null;

    const channelIds = [channelId];
    const isRelatedMapping = CommonUtils.localStringCompare(
      channelMappingType, constants.CHANNEL_MAPPING_TYPES.RELATED
    );

    if (isRelatedMapping && secondaryChannels?.length > 0) {
      // eslint-disable-next-line camelcase
      secondaryChannels.forEach(({ channel_id }) => channelIds.push(channel_id));
    }

    return channelIds;
  }

  /**
   * Returns all channelIds separated by comma of a book
   *
   * @param {String} channelId
   * @param {Array} secondaryChannels
   * @param {String} channelMappingType
   * @returns
   */
  static getCommaSeparatedChannelIds(channelId, secondaryChannels = [], channelMappingType) {
    const channelIds = CommonUtils.getAllChannelIds(channelId, secondaryChannels, channelMappingType);

    return Array.isArray(channelIds) ? channelIds.join(',') : null;
  }

  /**
   * Returns popular channel data
   *
   * @param {Array} courses
   * @returns
   */
  static constructPopularChannelsData(courses) {
    let popularChannels = [];

    if (courses && courses.length > 0) {
      popularChannels = courses.map(({
        type = '',
        details: {
          title,
          thumbnail,
          courseId,
          url,
          learnUrl = null,
          showExams = false,
          aiTutorUrl = null,
          exploreUrl = null,
          group = null,
          channelId
        }
      }) => ({
        type,
        title,
        thumbnailUrl: thumbnail,
        channelId: courseId || channelId,
        url,
        active: true,
        learnUrl,
        showExams,
        aiTutorUrl,
        exploreUrl,
        group
      }));
    }

    return popularChannels;
  }

  /**
   * Returns 'bundle' OR 'standalone_subscription' OR 'notAvailable'
   *
   * @returns {String}
   */
  static getChannelSubscriptionType = () => {
    const {
      BUNDLE,
      STANDALONE
    } = constants.CHANNEL_SUBSCRIPTION_TYPES;
    const user = getSnapshot(Framework.getStoreRegistry().getStore('user'));

    if (CommonUtils.isBundleSubscriber()) {
      return BUNDLE;
    }

    if (CommonUtils.hasSubscription(user, [constants.ACTIVE], [constants.ENTITLEMENTLEVEL_STUDY])) {
      return STANDALONE;
    }

    return constants.NOT_AVAILABLE;
  };

  /**
   * Returns the length of the remediation container
   *
   * @param {HTMLElement} targetElement - The target element to get the remediation container length from
   * @returns {Number}
   */
  static getRemediationContainerLength = (targetElement) => {
    const remediationContainer = targetElement?.querySelector('.grid-container-remediation-component');

    if (remediationContainer) {
      return remediationContainer?.children?.length;
    }

    return 0;
  };

  /**
   * Builds custom fields for instructor insights viewport events (886, 891).
   * Uses herobanner store directly, same pattern as View Channels Card viewport.
   *
   * @param {String} locationInApp
   * @param {String|null} assignmentId
   * @param {Object} flags
   * @returns {Object|null}
   */
  static getInstructorInsightsViewportCustomFields = (locationInApp, assignmentId, flags) => {
    const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = heroBanner?.getHeroBook?.() || {};

    if (CommonUtils.getIsStudent(heroBook)) {
      return null;
    }

    return {
      person_role_code: 'instructor',
      course_id: heroBook.course_id || null,
      course_section_id: heroBook.course_id || null,
      organization_id: heroBook.organizationId || null,
      book_id: heroBook.book_id || null,
      location_in_app: locationInApp,
      assignable_etext: heroBook.isAETEnabled ? 'true' : 'false',
      assigned_etext: heroBook.isAet ? 'true' : 'false',
      assignment_id: assignmentId || null,
      container_value: CommonUtils.getCurrentScreenEventLabel(flags),
      is_ghost_account: CommonUtils.isGhostAccount()
    };
  };

  /**
   * Triggers the View Difficult Topics event when the difficult questions block enters the viewport.
   * Same IntersectionObserver path as AET dashboard viewport tracking.
   *
   * @eventNumber 886
   * @param {HTMLElement} targetElement
   * @param {Object} flags
   * @returns {void}
   */
  static triggerDifficultQuestionsViewportEvent = (targetElement, flags) => {
    const assignmentId = targetElement?.dataset?.assignmentId || null;
    const locationInApp = targetElement?.dataset?.locationInApp
      || constants.AET_EVENT.LOCATION_IN_APP.UP_NEXT_AET;
    const customFields = CommonUtils.getInstructorInsightsViewportCustomFields(
      locationInApp,
      assignmentId,
      flags
    );

    if (!customFields) {
      return;
    }

    // Event: 886
    CommonUtils.dispatchGaEvent(
      constants.AET_EVENT.EVENT_CATEGORY.DASHBOARD,
      constants.EVENT_USER_INTERACTION,
      constants.AET_EVENT.EVENT_ACTION.VIEW_DIFFICULT_TOPICS,
      null,
      customFields
    );
  };

  /**
   * Triggers the View Struggling Students event when the struggling students block enters the viewport.
   * Same IntersectionObserver path as AET dashboard viewport tracking.
   *
   * @eventNumber 891
   * @param {HTMLElement} targetElement
   * @param {Object} flags
   * @returns {void}
   */
  static triggerStrugglingStudentsViewportEvent = (targetElement, flags) => {
    const assignmentId = targetElement?.dataset?.assignmentId || null;
    const locationInApp = targetElement?.dataset?.locationInApp
      || constants.AET_EVENT.LOCATION_IN_APP.UP_NEXT_AET;
    const customFields = CommonUtils.getInstructorInsightsViewportCustomFields(
      locationInApp,
      assignmentId,
      flags
    );

    if (!customFields) {
      return;
    }

    // Event: 891
    CommonUtils.dispatchGaEvent(
      constants.AET_EVENT.EVENT_CATEGORY.DASHBOARD,
      constants.EVENT_USER_INTERACTION,
      constants.AET_EVENT.EVENT_ACTION.VIEW_STRUGGLING_STUDENTS,
      null,
      customFields
    );
  };

  /**
   * Method to get locationInApp
   *
   * @param {Object} flags
   * @param {Array} target
   */
  static viewChannelsViewPort = (flags, targetElement) => {
    const target = targetElement?.dataset?.componentid;
    const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
    const { channelId } = getSnapshot(heroBanner);
    const template = heroBanner.bannerType || constants.HERO_BANNER_TYPES.MARKETING_TEMPLATE;
    const matchedValues = Object.entries(constants.VIEW_CHANNELS_CARD_SCROLL_EVENT)
      .filter(([key]) => target?.includes(key))
      .map(([, value]) => value);
    const isMatchedSection = matchedValues?.length > 0;

    if (isMatchedSection && !channelId && template === constants.HERO_BANNER_TYPES.BOOK
        && (matchedValues[0] === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.SUBSCRIPTION_STATUS || matchedValues[0] === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.RELATED_CHANNEL)) {
      // Event: 584
      CommonUtils.tiggerViewChannelsCardEvent(matchedValues[0], true, flags, CommonUtils.getRemediationContainerLength(targetElement));
    }
    if (isMatchedSection && channelId && (matchedValues[0] !== constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.POPULAR_CHANNELS_SLIDER && matchedValues[0] !== constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.REVIEW_REMEDIATION_CONTAINER)) {
      const tiggeredEvent = CommonUtils.cachedTiggeredEvents[channelId] || [];
      if (!tiggeredEvent.includes(matchedValues[0])) {
        tiggeredEvent.push(matchedValues[0]);
        CommonUtils.cachedTiggeredEvents[channelId] = tiggeredEvent;
        // Event: 584
        CommonUtils.tiggerViewChannelsCardEvent(matchedValues[0], true, flags, CommonUtils.getRemediationContainerLength(targetElement));
      }
    }
    if (isMatchedSection && channelId && matchedValues[0] === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.REVIEW_REMEDIATION_CONTAINER) {
      // Event: 584
      CommonUtils.tiggerViewChannelsCardEvent(matchedValues[0], true, flags, CommonUtils.getRemediationContainerLength(targetElement));

      // Event 866
      Framework.getEventManager().publish(AET_STORE_EVENTS.VIEW_QUESTION_REMEDIATION_OPTIONS, {});
    }
    if (isMatchedSection && (matchedValues[0] === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.POPULAR_CHANNELS_SLIDER || matchedValues[0] === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.POPULAR_CHANNELS_EMPTY_STATE)) { // checking only popular channel section
      let popularChannelConst = (channelId && template !== constants.HERO_BANNER_TYPES.BOOK) ? constants.POPULAR_CHANNELS_WITH_CHANNEL_ID : constants.POPULAR_CHANNELS_WITH_OUT_CHANNEL_ID;
      if (template === constants.HERO_BANNER_TYPES.MARKETING_TEMPLATE) {
        popularChannelConst = constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.POPULAR_CHANNELS_EMPTY_STATE;
      }
      if (!CommonUtils.cachedTiggeredEvents[popularChannelConst]) {
        CommonUtils.cachedTiggeredEvents[popularChannelConst] = matchedValues[0];
        // Event: 584
        CommonUtils.tiggerViewChannelsCardEvent(matchedValues[0], true, flags);
      }
    }
  };

  /**
   * Handles AET dashboard viewport impressions for instructor insight sections.
   *
   * Reads `data-componentid` on the intersecting element and dispatches at most one
   * event per cache key in `cachedTiggeredEvents`:
   *
   * - `DIFFICULT_QUESTIONS_CONTAINER` → View Difficult Topics (@eventNumber 886)
   * - `STRUGGLING_STUDENTS_CONTAINER` → View Struggling Students (@eventNumber 891)
   *
   * Cache keys include `event_action`, `assignmentId`, and `location_in_app` from
   * `data-assignment-id` / `data-location-in-app` on the wrapper element.
   *
   * @param {Object} flags Screen/feature flags for custom field builders.
   * @param {HTMLElement} targetElement Intersecting container from the observer callback.
   * @returns {void}
   */
  static viewAetDashboardViewPort = (flags, targetElement) => {
    const target = targetElement?.dataset?.componentid;
    if (target === constants.DIFFICULT_QUESTIONS_CONTAINER_COMPONENT_ID) {
      const { VIEW_DIFFICULT_TOPICS } = constants.AET_EVENT.EVENT_ACTION;
      const cacheKey = `${VIEW_DIFFICULT_TOPICS}:${targetElement?.dataset?.assignmentId}:${targetElement?.dataset?.locationInApp}`;
      if (!CommonUtils.cachedTiggeredEvents[cacheKey]) {
        CommonUtils.cachedTiggeredEvents[cacheKey] = true;
        // Event: 886
        CommonUtils.triggerDifficultQuestionsViewportEvent(targetElement, flags);
      }
      return;
    }
    if (target === constants.STRUGGLING_STUDENTS_CONTAINER_COMPONENT_ID) {
      const { VIEW_STRUGGLING_STUDENTS } = constants.AET_EVENT.EVENT_ACTION;
      const cacheKey = `${VIEW_STRUGGLING_STUDENTS}:${targetElement?.dataset?.assignmentId}:${targetElement?.dataset?.locationInApp}`;
      if (!CommonUtils.cachedTiggeredEvents[cacheKey]) {
        CommonUtils.cachedTiggeredEvents[cacheKey] = true;
        // Event: 891
        CommonUtils.triggerStrugglingStudentsViewportEvent(targetElement, flags);
      }
    }
  };

  /**
   * Returns comma-separated assignable_etext and assigned_etext values for student bookshelf books.
   *
   * @returns {Object}
   */
  static getStudentBookshelfEtextCustomFields = () => {
    const bookshelf = Framework.getStoreRegistry().getStore('bookshelf');
    const { books = [] } = getSnapshot(bookshelf) || {};
    const studentBooks = books.filter(book => CommonUtils.getIsStudent(book));

    return {
      assignable_etext: studentBooks
        .map(book => (book.isAETEnabled ? 'true' : 'false'))
        .join(','),
      assigned_etext: studentBooks
        .map(book => (book.isAet ? 'true' : 'false'))
        .join(',')
    };
  };

  /**
   * Returns the video count for channels card remediation assets.
   *
   * @returns {String}
   */
  static getChannelsCardVideoCount = () => {
    try {
      const channelRemediationByBook = Framework.getStoreRegistry().getStore('channelRemediationByBook');
      const { byChaptersAssets = [] } = getSnapshot(channelRemediationByBook) || {};
      const videoCount = byChaptersAssets.filter(
        asset => constants.VIDEO_ASSET_TYPES.includes(asset?.type)
      ).length;

      return videoCount > 0 ? CommonUtils.toString(videoCount) : '';
    } catch (error) {
      return '';
    }
  };

  /**
   * Appends custom fields for View Channels Card events.
   *
   * @param {Object} customFields
   * @returns {Object}
   */
  static appendViewChannelsCardCustomFields = (customFields = {}) => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner?.getHeroBook?.() || {};
    const viewChannelsCardCustomFields = {
      ...customFields,
      video_count: CommonUtils.getChannelsCardVideoCount()
    };

    if (!CommonUtils.getIsStudent(heroBook)) {
      return viewChannelsCardCustomFields;
    }

    return {
      ...viewChannelsCardCustomFields,
      ...CommonUtils.getStudentBookshelfEtextCustomFields()
    };
  };

  /**
   * Appends student bookshelf etext custom fields for channel card click events.
   *
   * @param {Object} customFields
   * @returns {Object}
   */
  static appendStudentChannelCardEtextCustomFields = (customFields = {}) => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner?.getHeroBook?.() || {};

    if (!CommonUtils.getIsStudent(heroBook)) {
      return customFields;
    }

    return {
      ...customFields,
      ...CommonUtils.getStudentBookshelfEtextCustomFields()
    };
  };

  /**
   * Resolves location_in_app for View Channels Card events on AeT suggested review.
   * Student logins use Suggested_review_* values; instructors keep scroll-event values.
   *
   * @param {String} locationInApp
   * @returns {String}
   */
  static getViewChannelsCardLocationInApp = (locationInApp) => {
    const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = heroBanner?.getHeroBook?.() || {};

    if (!CommonUtils.getIsStudent(heroBook)) {
      return locationInApp;
    }

    const { VIEW_CHANNELS_CARD_SCROLL_EVENT, AET_EVENT } = constants;
    const { LOCATION_IN_APP } = AET_EVENT;

    if (locationInApp === VIEW_CHANNELS_CARD_SCROLL_EVENT.REMEDIATION_CONTAINER) {
      return LOCATION_IN_APP.SUGGESTED_REVIEW_UP_NEXT_AET;
    }
    if (locationInApp === VIEW_CHANNELS_CARD_SCROLL_EVENT.REVIEW_REMEDIATION_CONTAINER) {
      return LOCATION_IN_APP.SUGGESTED_REVIEW_REVIEW_AET;
    }

    return locationInApp;
  };

  /**
   * Triggers the View Channels Card event when a channels card section enters the viewport or on initial load
   * @eventNumber 584
   * @param {String} locationInApp
   * @param {Boolean} isInitialLoad
   * @param {Object} flags
   * @param {Number} eventValue
   * @returns {void}
   */
  static tiggerViewChannelsCardEvent = (locationInApp, isInitialLoad = false, flags, eventValue = null) => {
    const heroBanner = Framework.getStoreRegistry().getStore('herobanner');
    const resolvedLocationInApp = CommonUtils.getViewChannelsCardLocationInApp(locationInApp);
    const { channelId } = getSnapshot(heroBanner);
    const template = heroBanner.bannerType || constants.HERO_BANNER_TYPES.MARKETING_TEMPLATE;
    if (template === constants.HERO_BANNER_TYPES.CHANNEL
      || template === constants.HERO_BANNER_TYPES.BOOK_WITH_CHANNEL
      || template === constants.HERO_BANNER_TYPES.BOOK
      || template === constants.HERO_BANNER_TYPES.MARKETING_TEMPLATE
    ) {
      const customFields = {
        is_ghost_account: CommonUtils.isGhostAccount(),
        channel_id: CommonUtils.getChannelIdForChannelsCardEvent(resolvedLocationInApp, channelId),
        container_value: CommonUtils.getCurrentScreenEventLabel(flags)
      };
      customFields.location_in_app = resolvedLocationInApp;

      if (eventValue) {
        customFields.event_value = CommonUtils.toString(eventValue);
      }

      const eventAction = isInitialLoad
        ? constants.CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_VIEW_EVENT_ACTION
        : constants.CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_CLICK_EVENT_ACTION;
      const channelCardCustomFields = isInitialLoad
        ? CommonUtils.appendViewChannelsCardCustomFields(customFields)
        : CommonUtils.appendStudentChannelCardEtextCustomFields(customFields);
      // Event: 584
      CommonUtils.dispatchGaEvent(
        constants.AUTH_HOME_CATEGORY,
        constants.EVENT_AUTHHOMEEVENT,
        eventAction,
        constants.CHANNEL_CARD_EVENT.CHANNEL_CARD_ELEMENT_EVENT_LABEL,
        channelCardCustomFields
      );
    }
  };

  /**
   * Method to get ChannelId for ChannelsCardEvent
   * @param {String} locationInApp
   * @param {String} channelId
   */
  static getChannelIdForChannelsCardEvent = (locationInApp, channelId = null) => {
    let channelIdForChannelsCardEvent = channelId;
    if (locationInApp === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.POPULAR_CHANNELS_SLIDER || locationInApp === constants.VIEW_CHANNELS_CARD_SCROLL_EVENT.POPULAR_CHANNELS_EMPTY_STATE) {
      channelIdForChannelsCardEvent = null;
    }
    return channelIdForChannelsCardEvent;
  };

  /**
   * To capitalize the first letter
   *
   * @param {*} string
   * @returns
   */
  static capitalize = str => (str && String(str[0]).toUpperCase() + String(str).slice(1)) || '';

  /**
   * Returns true if course is part of bookshelf
   *
   * @param {Object} book
   * @returns {Boolean}
   */
  static handleLMSBookMatchByCourseId = (book, courseId) => (
    book.course_id === courseId && !CommonUtils.isExpiredBook(book)
  );

  static isLMSCourse = () => CommonUtils.isQueryParamAvailable(constants.IS_LMS, 'y');

  static callTOPDPRedirectAPI = (match, book = null) => {
    const mlmBookData = book || CommonUtils.getMLMRouteBookData(match) || {};
    const courseRegistrarId = mlmBookData?.courseId || match?.params?.id || null;
    const event = {
      data: {
        courseId: courseRegistrarId
      }
    };
    return pdpRedirectService
      .handleEvent(constants.PDP_SERVICE_REQUESTED, event)
      .then((response) => {
        pdpRedirectService.handleResponse(response);
        return { ...response.data, courseId: courseRegistrarId };
      })
      .catch((error) => {
        pdpRedirectService.handleError(error);
        console.warn('Error fetching PDP Redirect:', error);
        Framework.redirectToErrorPage('SUBSCRIPTION_GENERIC');
      });
  };

  /**
   * Fetches countryCode for a course via the PDP Redirect API.
   *
   * @param {string} courseId - Registrar course id
   * @param {Function} onSuccess - Called with countryCode from the PDP Redirect response
   * @param {Function} onError - Called when the request fails or countryCode is missing
   */
  static getCountryCodeFromCourse = (courseId, onSuccess, onError) => {
    if (!courseId) {
      if (onError) {
        onError(new Error('Missing courseId'));
      }

      return;
    }

    pdpRedirectService
      .handleEvent(constants.PDP_SERVICE_REQUESTED, { data: { courseId } })
      .then((response) => {
        pdpRedirectService.handleResponse(response);
        const { countryCode } = response?.data || {};

        if (countryCode) {
          onSuccess?.(countryCode);

          return;
        }

        onError?.(new Error('Missing countryCode in PDP redirect response'));
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('Error fetching country code from PDP Redirect:', error);

        onError?.(error);
      });
  };

  static handleLMSFlow = (match, redirectUrl = '', flags = {}) => {
    CommonUtils.callTOPDPRedirectAPI(match)
      .then((data) => {
        if (data) {
          const url = CommonUtils.getPDPPageURLV2(data, redirectUrl, flags);
          if (url) {
            console.log('Redirecting to PDP URL:', url);
            window.open(url, '_self');
          }
        }
      })
      .catch((error) => {
        console.error('Error in callTOPDPRedirectAPI:', error);
      });
  };

  /**
  * To get PDP(PMC) product url in case of LMS flow
  * @param {Object} data - Contains book details like isbn, countryCode, isIA
  * @param {string} registrarSectionId - The registrar section ID
  * @return {string} - The constructed PDP page URL
  * */
  static getPDPPageURLV2 = (data, redirectUrl = '', flags = {}) => {
    let returnUrl = null;
    const {
      isbn, countryCode, isIA, courseId, isAet
    } = data;
    const isAetPDPRedirectionEnabled = flags.enableUSAetPDPRedirection || isAet;
    const baseURLs = {
      US: `${env.BOOKSHELF_CONFIG.pmcSearch}${isAetPDPRedirectionEnabled ? '/en-us/subject-catalog/p' : '/en-us/pearsonplus/p'}`,
      UK: `${env.BOOKSHELF_CONFIG.pmcSearch}/en-gb/subject-catalog/p`,
      GB: `${env.BOOKSHELF_CONFIG.pmcSearch}/en-gb/subject-catalog/p`, // service later changed UK to GB, we keep both for safety
      CA: `${env.BOOKSHELF_CONFIG.pmcSearch}/en-ca/subject-catalog/p`,
      AU: `${env.BOOKSHELF_CONFIG.pmcSearch}/en-au/subject-catalog/p`,
      NZ: `${env.BOOKSHELF_CONFIG.pmcSearch}/en-nz/subject-catalog/p`
    };

    if (baseURLs[countryCode]) {
      const baseURL = baseURLs[countryCode];
      const source = isIA ? 'LMSIA' : 'LMSNonIA';
      let queryParams = `?source=${source}&registrarSectionId=${courseId}`;

      // Append redirectUrl if provided
      if (redirectUrl) {
        queryParams += `&redirectUrl=${redirectUrl}`;
      }

      returnUrl = `${baseURL}/${isbn}${queryParams}`;
    } else if (env.STORE_URL[countryCode]) {
      const baseURL = env.STORE_URL[countryCode];
      returnUrl = `${baseURL}/aet?isbn13=${isbn}&courseId=${courseId}`;
      if (redirectUrl) {
        returnUrl += `&redirectUrl=${redirectUrl}`;
      }
    }
    console.warn(`Unsupported country code: ${countryCode}`);

    return returnUrl;
  };

  // Utility function to remove underscores and add spaces
  static removeUnderscoresAndAddSpaces = (data) => {
    try {
      if (!data || typeof data !== 'object') {
        return {}; // Return an empty object if data is null, undefined, or not an object
      }
      return Object.keys(data).reduce((acc, key) => {
        if (key && typeof key === 'string') {
          // Replace underscores with spaces and capitalize each word
          const formattedKey = key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
          acc[formattedKey] = data[key];
        }
        return acc;
      }, {});
    } catch (error) {
      console.error('An error occurred while processing the data:', error);
      return {}; // Return an empty object in case of an error
    }
  };


  static mapDiagnosticsInfo(diagnosticsInfo = {}, gpsUserData = {}, courseRegistrarId, productModelName = '', isStudent) {
    try {
      const sections = Array.isArray(diagnosticsInfo.sections) ? diagnosticsInfo.sections : [];
      const updatedSections = [];

      sections.forEach((section) => {
        // defensive checks
        if (!section || typeof section !== 'object' || !section.title || !section.data) {
          console.warn('Invalid section in diagnosticsInfo:', section);
          return;
        }
        // remove all underscores from keys
        let transformedData = CommonUtils.removeUnderscoresAndAddSpaces(section.data);
        if (!transformedData || typeof transformedData !== 'object') {
          console.warn('Invalid data in section:', section);
          return;
        }

        // remove http or https from url
        if (CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.DIAGNOSTICS_INFORMATION)) {
          if (transformedData[constants.DIAGNOSTIC_HEADINGS.URL]) {
            transformedData[constants.DIAGNOSTIC_HEADINGS.URL] = transformedData[constants.DIAGNOSTIC_HEADINGS.URL].replace(/^https?:\/\//, '');
          }
          updatedSections.push({
            ...section,
            data: transformedData
          });
        } else if ( // to add Pearson User ID in the USER_INFORMATION and YOUR_ACCOUNTS sections
          CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.USER_INFORMATION)
          || CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.YOUR_ACCOUNTS)
        ) {
          const { [constants.DIAGNOSTIC_HEADINGS.PEARSON_USER_ID]: existingPearsonUserId, ...restData } = transformedData;
          const pearsonUserId = existingPearsonUserId || gpsUserData.id || '';
          if (CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.USER_INFORMATION)) {
            transformedData = {
              [constants.DIAGNOSTIC_HEADINGS.PEARSON_USER_ID]: pearsonUserId,
              ...restData
            };
          } else if (CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.YOUR_ACCOUNTS)) {
            transformedData = {
              ...restData,
              [constants.DIAGNOSTIC_HEADINGS.PEARSON_USER_ID]: pearsonUserId
            };
          }
          updatedSections.push({
            ...section,
            data: transformedData
          });
        } else if (
          CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.ORGANIZATIONAL_DATA)
          || CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.LAUNCH_CONTEXT_KEY_INFORMATION)) {
          updatedSections.push({ // add nodivider
            ...section,
            type: 'no-divider',
            data: transformedData
          });
        } else if (CommonUtils.localStringCompare(section.title, constants.DIAGNOSTIC_HEADINGS.COURSE_INFORMATION)) {
          transformedData = { // add productmodel
            ...transformedData,
            [constants.DIAGNOSTIC_HEADINGS.PRODUCT_MODEL_TYPE]: productModelName || ''
          };
          updatedSections.push({
            ...section,
            data: transformedData
          });
          // construct clientinfo
          const clientInfoData = {
            [constants.DIAGNOSTIC_HEADINGS.CLIENT_OS]: `${osName || ''} ${osVersion || ''}`,
            [constants.DIAGNOSTIC_HEADINGS.INTERNET_BROWSER]: `${CommonUtils.getBrowserName() || ''} (${fullBrowserVersion || ''})`
          };
          if (!isStudent) { // add device type only for instructors
            clientInfoData[`${constants.DIAGNOSTIC_HEADINGS.DEVICE}`] = CommonUtils.getDeviceType() || '';
          }
          updatedSections.push({
            title: constants.DIAGNOSTIC_HEADINGS.CLIENT_INFORMATION,
            data: clientInfoData
          });
        } else {
          updatedSections.push({ // final push
            ...section,
            data: transformedData
          });
        }
      });
      return {
        ...diagnosticsInfo,
        sections: updatedSections
      };
    } catch (error) {
      console.error('Error mapping diagnostics information:', error);
      return null; // Return original diagnosticsInfo in case of error
    }
  }

  /* Returns match object if the route is matched else returns null;
   *
   * @param {Array} routes
   * @param {Object} location
   * @returns
   */
  static getMatchedRoute = (routes = [], location = {}) => {
    let matchedRoute = null;

    routes.find((route) => {
      const foundRoute = matchPath(
        location.pathname,
        {
          path: route,
          exact: true
        }
      );

      if (!foundRoute) {
        return false;
      }

      matchedRoute = foundRoute;

      return true;
    });

    return matchedRoute;
  };

  /**
   * Add query param in channel URL
   *
   * @param {String} URL
   * @returns
   */
  static addQueryParameterWithHash = (URL = '', queryString = '') => {
    if (!URL) {
      return;
    }
    const [baseAndQuery, fragment] = URL.split('#');
    if (!baseAndQuery) {
      return;
    }

    let separator = '';
    if (queryString) {
      // eslint-disable-next-line max-len, no-const-assign
      separator = URL?.includes(constants.URL_QUERY_START_INDICATOR) ? constants.URL_QUERY_SEPARATOR : constants.URL_QUERY_START_INDICATOR;
    }

    const newQuery = `${baseAndQuery}${separator}${queryString}`;
    // eslint-disable-next-line consistent-return
    return fragment ? `${newQuery}#${fragment}` : newQuery;
  };

  static dispatchQRcodeViewEvent = (eventLabel, locationInApp) => {
    const {
      EVENT_ACTION
    } = constants.QR_CODE_VIEW_EVENT;

    const customFields = {
      location_in_app: locationInApp,
      is_ghost_account: CommonUtils.isGhostAccount()
    };

    CommonUtils.dispatchGaEvent(
      constants.AUTH_HOME_CATEGORY,
      constants.EVENT_USER_INTERACTION,
      EVENT_ACTION,
      eventLabel,
      customFields
    );
  };

  /**
   * Initialize Message Receiver
   *
   * @param {*} listenerCallback
   */
  static initializeMsgReceiver = (listenerCallback) => {
    window.addEventListener(
      'message', event => CommonUtils.handlePostMessage(event, listenerCallback)
    );
  };

  /**
   * Handle post message
   * @param {*} event
   * @param {*} listenerCallback
   */
  static handlePostMessage = (event, listenerCallback) => {
    if (event && event.data) {
      switch (event.data.type) {
        case constants.POST_MESSAGE_TYPE.CHANNEL: {
          const { action } = event.data;
          listenerCallback(action || null);
          break;
        }
        default:
          break;
      }
    }

    handleChatBotIframeEvent(event);
  };

  /**
   * Control to show/Hide the home icon in Ereader
   *
   * @param {*} config
   * @returns
   */

  static showBackNav = (config) => {
    const { isGlobalNavEnabled = false } = config;

    return { showBackNav: !isGlobalNavEnabled };
  };

  /**
   * Returns default LDCustomAttribute
   *
   * @returns {Object}
   */
  static defaultLDCustomAttribute = () => (
    { isScreenResBelow1024: CommonUtils.isScreenResoultionBelow(constants.TABLET_BREAKPOINT_1024) }
  );

  /**
   * Returns true should channel mini player be enabled
   *
   * @param {Object} args
   * @returns {Boolean}
   */
  static shouldEnableChannelMiniPlayer = (args = {}) => {
    const {
      primaryChannelId,
      channelName,
      featureFlags = {},
      isStudyPrepEnabled,
      channelAccess
    } = args;

    return (
      featureFlags.enableChannelMiniPlayer
      && primaryChannelId
      && (typeof channelName === 'string' && channelName.trim().length > 0)
      && CommonUtils.showStudyPrepInEReader(channelAccess, isStudyPrepEnabled)
    );
  };

  /**
   * Returns expand or collapse button's tooltip text
   *
   * @param {Boolean} isNavOpen
   * @returns
   */
  static getExpandCollapseTooltipText = (isNavOpen = true) => {
    const language = Framework.getStoreRegistry().getStore('language');

    if (isNavOpen) {
      return language?.getMessage('globalnav.tooltip.hideNavText');
    }

    return language.getMessage('globalnav.tooltip.showNavText');
  };

  static isAetRoute = () => (window.location.pathname.includes('/aet/home'));

  static getIsStudent(heroBook) {
    const { courseUserRole = constants.USER_ROLE_STUDENT } = heroBook || {};
    return this.localStringCompare(courseUserRole, constants.USER_ROLE_STUDENT);
  }

  static isInsideIframe = () => constants.IFRAME === PathUtils.getQueryParameterbyName('mode');

  static redirectTo(url) {
    // If URL already has query parameters, merge them with window.location.search
    // Otherwise, just append window.location.search
    const [baseUrl, existingQuery] = url.split('?');
    const currentSearch = window.location.search || '';

    // If no existing query params in URL and no current search, return as is
    if (!existingQuery && !currentSearch) {
      return url;
    }

    // Parse existing query params from URL
    const existingParams = new URLSearchParams(existingQuery || '');

    // Parse current query params from window.location
    const currentParams = new URLSearchParams(currentSearch);

    // Merge: current params override existing ones (or vice versa - you can change this logic)
    // For now, existing URL params take precedence, then add any new ones from current location
    currentParams.forEach((value, key) => {
      // Only add if not already present in existing params
      if (!existingParams.has(key)) {
        existingParams.append(key, value);
      }
    });

    const mergedQuery = existingParams.toString();
    return mergedQuery ? `${baseUrl}?${mergedQuery}` : baseUrl;
  }

  static transformToAssignmentTags(currentAssignment) {
    if (!currentAssignment && !currentAssignment?.length > 0) {
      return [];
    }

    return currentAssignment;
  }

  static formatLocalDateTime = (localDateTimeString) => {
    if (!localDateTimeString) return null;
    try {
      const date = new Date(localDateTimeString);
      // Format date part (e.g., "Aug 03, 2025")
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];
      const month = monthNames[date.getMonth()];
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      // Format time part (e.g., "02.30pm")
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';

      // Convert to 12-hour format
      hours %= 12;
      hours = hours || 12;
      const formattedHours = String(hours).padStart(2, '0');

      return `${month} ${day}, ${year} @ ${formattedHours}:${minutes} ${ampm}`;
    } catch (e) {
      console.warn('Error formatting local datetime:', e);
      return null;
    }
  };

  /**
   * Returns true if the book is a digital sample book
   *
   * @param {Object} book
   * @returns
   */
  static isDigitalSampleBook = (book = {}) => {
    // eslint-disable-next-line camelcase
    const { product_entitlements } = book;
    // eslint-disable-next-line camelcase
    const { business_model_code } = product_entitlements || {};
    const digitalSampleBMCs = [
      constants.DIGITAL_SAMPLE_MARKETING, constants.DIGITAL_SAMPLE_SELF, constants.DIGITAL_SAMPLE_REP
    ];

    return digitalSampleBMCs.includes(business_model_code);
  };

  /**
   * Returns true if AET is allowed for the BMC
   * @param {String} bmc
   * @returns {Boolean}
   */
  static isAllowedAETBMC = (book, flags = {}) => {
    const { instructorAeTBmcList = [] } = flags;

    if (instructorAeTBmcList?.length === 0) {
      return true;
    }

    return instructorAeTBmcList.some(
      bmc => CommonUtils.localStringCompare(bmc, book?.product_entitlements?.business_model_code)
    );
  };

  /**
   * Returns true if the BMC is in the allowed list
   * @param {String} bmc
   * @returns {Boolean}
   */
  static isAllowedBMC = (book, allowedBMCs = []) => allowedBMCs.some(
    bmc => CommonUtils.localStringCompare(bmc, book?.product_entitlements?.business_model_code)
  );

  /**
   * Returns true if the book type is in the allowed list
   * @param {Object} book
   * @param {Array} allowedBookTypes
   * @returns {Boolean}
   */
  static isAllowedBookType = (book, allowedBookTypes = []) => {
    if (!book || !Array.isArray(allowedBookTypes)) {
      return false;
    }

    // If allowedBookTypes is empty, then allow all book types
    if (allowedBookTypes.length === 0) {
      return true;
    }

    return allowedBookTypes.some(
      bookType => CommonUtils.localStringCompare(bookType, book?.book_type)
    );
  };

  /**
   * Returns true if AET is enabled for the book
   *
   * @param {Object} book
   * @returns
   */
  static isAETEnabled = (book = {}, flags = {}) => {
    const { enableAET } = flags;
    const { isAETEnabled, isAet } = book;
    const isStudent = this.getIsStudent(book);

    // Student -> AET is enabled at course level, course already has assignments
    if (isStudent) {
      return isAet;
    }

    // If course_id is not present for instructor then AET is disabled
    if (!book?.course_id) {
      return false;
    }

    // TA --> Enable Instructor Dashboard when AET is enabled via feature flags
    if (CommonUtils.isTACourse(book)) {
      return enableAET && isAETEnabled;
    }


    // If instructorAeTBmcList is empty, then allow all BMCs
    const isAllowedAETBMC = CommonUtils.isAllowedAETBMC(book, flags);

    // Instructor -> AET is enabled by feature flag, book level BMC is in the feature flag list and flag at book level
    return enableAET && isAETEnabled && isAllowedAETBMC;
  };

  /**
   * Returns static profressData
   *
   * @returns
   */
  static progressCardData = () => {
    const profressData = {
      dueDate: null,
      great: 0,
      greatPercentage: 0,
      notAttmpted: 0,
      notAttmptedPercentage: 0,
      gettingThere: 0,
      gettingTherePercentage: 0,
      needReview: 0,
      needReviewPercentage: 0,
      completedOnTime: 0,
      completedOnTimePercentage: 0,
      completedLate: 0,
      completedLatePercentage: 0,
      inProgress: 0,
      inProgressPercentage: 0,
      notStarted: 0,
      notStartedPercentage: 100
    };

    return profressData;
  };

  /**
   * Method to prepare ComprehensionData for badge
   * @param {Object} studentProgress
   * @returns
   */
  static getComprehensionData = (studentProgress) => {
    if (!studentProgress?.status) {
      return null;
    }

    const language = Framework.getStoreRegistry().getStore('language');
    const progressList = {
      COMPLETED: language.getMessage('aet.home.progressCompletedOnTimeLabel'),
      IN_PROGRESS: language.getMessage('aet.home.progressInProgressLabel'),
      NOT_STARTED: language.getMessage('aet.home.NotAttemptedLabel'),
      COMPLETED_LATE: language.getMessage('aet.home.progressCompletedPastDueLabel')
    };

    const comprehensionData = {
      comprehension: `${progressList[studentProgress.status]} ${studentProgress?.questionsAttempted || 0}/${studentProgress?.questionsAssigned || 0}`,
      status: constants.PROGRESS_STATUS[studentProgress.status],
      showIcon: false,
      showTooltip: CommonUtils.localStringCompare(studentProgress.status, constants.PROGRESS_STATUS_IDS.COMPLETED)
    };

    return comprehensionData;
  };

  /**
   * Method to check icon need to show or not
   * @param {String} status
   */
  static isShowIcon = (status = null) => !CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS.COMPLETED);

  /**
   * Method to get tooltip data
   * @param {String} status
   */
  static getTooltipSegments = (completedOnTimeQuestions, completedLateQuestions, status) => {
    const isCompleted = CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS.COMPLETED);
    const isCompletedLate = CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS.COMPLETED_PAST_DUE)
    || CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS_IDS.COMPLETED_LATE);

    if (!isCompleted && !isCompletedLate) {
      return null;
    }

    if (!completedOnTimeQuestions && !completedLateQuestions) {
      return null;
    }

    const language = Framework.getStoreRegistry().getStore('language');

    const performanceTooltip = [
      {
        label: language.getMessage('aet.home.progressCompletedOnTimeLabel'),
        value: completedOnTimeQuestions,
        color: statusColorUtils.getTextColor(constants.BADGE_TOOLTIP_STATUS.COMPLETED_ON_TIME)
      },
      {
        label: language.getMessage('aet.home.progressCompletedPastDueLabel'),
        value: completedLateQuestions,
        color: statusColorUtils.getTextColor(constants.BADGE_TOOLTIP_STATUS.COMPLETED_PAST_DUE)
      }
    ];

    return performanceTooltip;
  };

  /**
   * Get current user id
   * @returns {String} - Current user id
   */
  static getCurrentUserId = () => {
    const user = Framework.getStoreRegistry().getStore('user');
    const { id } = getSnapshot(user);

    return id || window?.piSession?.userId();
  };

  /**
   * Get assessmentId using assignmentId
   * @param {String} assignmentId
   * @param {String} questionId
   * @returns {String} - assessmentId
   */
  static getAssessmentId = (assignmentId, questionId) => {
    if (!assignmentId || !questionId) {
      return null;
    }
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const { assignments } = getSnapshot(assignmentStore);
    const assignment = assignments.filter(e => e.assignmentId === assignmentId);
    if (!assignment?.length > 0 && !assignment[0]?.mcqs?.length > 0) {
      return null;
    }
    const mcqQuestion = assignment[0].mcqs.filter(mcq => mcq.questionId === questionId);
    if (!mcqQuestion?.length > 0) return null;

    return mcqQuestion[0].assessmentId || null;
  };

  /**
   * Method to combine complete status
   * @param {String} status
   * @param {String} status
   * @returns {String} - Combined status
   */
  static getCustomMergeCompleteStatus = (status, upperCase = false) => {
    if (!status) {
      return null;
    }

    if (CommonUtils.localStringCompare(status, constants.PROGRESS_STATUS_IDS.COMPLETED_LATE)) {
      return upperCase ? constants.PROGRESS_STATUS.COMPLETED.toUpperCase() : constants.PROGRESS_STATUS.COMPLETED;
    }

    return status;
  };

  static parseDateString(dateStr) {
    if (!dateStr) return null;
    const localDateStr = dateStr.replace(/-/g, '/');
    return new Date(localDateStr);
  }

  /**
   * Method to get custom fields for library view event
   * Event 580
   * @param {String} location_in_app
   * @param {Boolean} isL2
   * @returns {Object} - Custom fields for library view event
   */
  static getLibraryViewEventCustomFields = (location_in_app, isL2 = false) => {
    const heroStore = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = heroStore.getHeroBook();
    let { course_id = null, organizationId = null, book_id = null } = heroBook || {};
    const isStudent = CommonUtils.getIsStudent(heroBook);
    const { EDUCATOR_AET, STUDENT_AET } = constants.AET_EVENT.LOCATION_IN_APP;
    const locationInApp = isStudent ? STUDENT_AET : EDUCATOR_AET;

    if (isL2) {
      course_id = null;
      organizationId = null;
      book_id = null;
    }

    return {
      person_role_code: isStudent ? 'student' : 'instructor',
      course_id,
      course_section_id: course_id,
      organization_id: organizationId,
      book_id,
      location_in_app: locationInApp
    };
  };

  static safeParseJsonArray(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    try {
      return JSON.parse(value);
    } catch (e) {
      console.warn('Failed to parse JSON array:', e);
      return [];
    }
  }

  /** * Converts a letter (A-Z) to its position number (1-26) * @param {string} letter - Single letter A-Z (case-insensitive) * @returns {number} Position number (1 for A, 2 for B, etc.) */
  static letterToNumber = letter => letter && letter.toUpperCase().charCodeAt(0) - 64;

  /**
   * Check if due date is greater than current date
   * @param {String} dueDate
   * @returns {Boolean} - true/false
   */
  static isDueDatePast(dueDate = null) {
    if (!dueDate) return false;

    return new Date(dueDate) > new Date();
  }

  // Home2 -> Instructor -> AET not enabled -> Not an MLM book -> show course card and eText card without the body.
  /**
   * Returns true if it is a non-MLM instructor deeplink launch
   * @returns {Boolean} - true/false
   */
  static isNonMlmInstructorDeeplinkLaunch = () => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook() || {};
    const isStudent = CommonUtils.getIsStudent(heroBook);
    const isInstructor = !isStudent;
    const contextId = LmsLaunchContext.getContextId();
    const BMC = heroBook?.product_entitlements?.business_model_code;
    const isMLMcourse = CommonUtils.isMyLabSubscription(heroBook?.course_id, BMC);

    return !!contextId && isInstructor && !isMLMcourse;
  };

  static generateSyllabusLocalStorageKey = () => {
    const userId = CommonUtils.getCurrentUserId();
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook() || {};
    const { course_id = null } = heroBook;

    return userId && course_id ? `aet_syllabus_${userId}_${course_id}` : null;
  };

  /**
   * Convert the resources object to a boolean object
   * @param {Object} resources
   * @returns {Object} - The boolean object
   */
  static convertBooleanObject(resources = {}) {
    const booleanObject = { ...resources };
    const covertKey = [{
      name: constants.OFF,
      value: false
    },
    {
      name: constants.OFF_DISABLED,
      value: false
    }, {
      name: constants.ON,
      value: true
    }];
    Object.keys(booleanObject).forEach((key) => {
      if (typeof (booleanObject[key]) === 'string') {
        covertKey.forEach(({ name, value }) => {
          if (CommonUtils.localStringCompare(booleanObject[key], name)) {
            booleanObject[key] = value;
          }
        });
      }
    });

    return booleanObject;
  }

  /**
 * Opens a PDP URL, handling dynamic PDP page URL for LMS flow
 * @param {string} launchURL - The URL to open (may be DYNAMIC_PDP_PAGE_URL)
 * @param {Object} bookData - Book data containing course_id
 * @param {string} target - Window target (_blank, _self, etc.)
 * @param {Object} flags - Feature flags from state
 */
  static openPDPUrl = (launchURL, bookData, target = '_blank', flags = {}) => {
    if (!launchURL) return;
    if (launchURL === constants.DYNAMIC_PDP_PAGE_URL) {
      // LMS flow - resolve dynamic PDP URL
      CommonUtils.callTOPDPRedirectAPI(null, { ...bookData, courseId: bookData.course_id })
        .then((data) => {
          if (data) {
            const pdpUrl = CommonUtils.getPDPPageURLV2(data, '', flags);
            if (pdpUrl) {
              window.open(pdpUrl, target);
            }
          }
        })
        .catch((error) => {
          console.warn('Error in callTOPDPRedirectAPI:', error);
        });
    } else {
      // Direct URL flow
      window.open(launchURL, target);
    }
  };

  /**
   * Semantic sorting of array of strings. This is used to sort the array of strings in a semantic way.
   *
   * @param {*} arr
   * @returns {Array} - The sorted array
   */
  static semanticSorting = (arr = []) => {
    if (!Array.isArray(arr) || arr.length === 0) return [];

    return [...arr].sort((a, b) => {
      const as = a.split('.').map(Number);
      const bs = b.split('.').map(Number);
      const len = Math.max(as.length, bs.length);
      for (let i = 0; i < len; i += 1) {
        const av = as[i] ?? 0;
        const bv = bs[i] ?? 0;
        if (av !== bv) return av - bv;
      }

      return 0;
    });
  };

  /**
   * Get assignment title validation messages
   * @returns {Object} - Assignment title validation messages
   */
  static getAssignmentTitleValidationMessages = () => {
    const language = Framework.getStoreRegistry().getStore('language');

    return {
      titleRequired: language.getMessage('aet.assignments.titleValidation.titleRequired'),
      titleTooLong: language.getMessage('aet.assignments.titleValidation.titleTooLong'),
      titleInvalidChar: language.getMessage('aet.assignments.titleValidation.titleInvalidChar'),
      titleDuplicate: language.getMessage('aet.assignments.titleValidation.titleDuplicate')
    };
  };

  /**
   * Get the ask AI prompt from session storage
   * @returns {String} - The ask AI prompt or null if not found
   */
  static getAskAIPrompt = (selectedPanel) => {
    if (selectedPanel !== constants.DRAWER_PANEL_ASK_AI) {
      return null;
    }

    const prompt = CommonUtils.getSessionStorge(constants.ASK_AI_PROMPT_KEY);

    return prompt || null;
  };

  /**
   * Performance report request saved by the student Assignments tab before redirecting to eReader.
   * Read once: the key is cleared so a refresh opens the assignment without the report.
   * @param {string} assignmentId - assignmentId query param of the eReader launch
   * @returns {{assignmentId: string, showPerformanceReport: boolean}|null}
   */
  static getPerformanceReportAssignment = (assignmentId) => {
    const savedAssignmentId = CommonUtils.getSessionStorge(constants.PERFORMANCE_REPORT_ASSIGNMENT_KEY);

    CommonUtils.clearSessionStorge(constants.PERFORMANCE_REPORT_ASSIGNMENT_KEY);

    if (!CommonUtils.localStringCompare(savedAssignmentId, assignmentId)) {
      return null;
    }

    return { assignmentId, showPerformanceReport: true };
  };

  /*
   * Count the number of assigned assignments
   * @returns {Number} - The number of assigned assignments
   */
  static countAssignedAssignments = () => {
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const { assignments } = getSnapshot(assignmentStore);
    return assignments?.length || 0;
  }

  static getCourseStartDateFromHeroBook = () => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook() || {};
    const { courses = [], course_id: activeCourseId } = heroBook;
    if (!courses.length) return null;
    const row = activeCourseId ? courses.find(course => course.course_id === activeCourseId) : null;
    return (row || courses[0]).course_start_date || null;
  };

  /** Returns the active course end date from the hero book, or null when unavailable. */
  static getCourseEndDateFromHeroBook = () => {
    const herobanner = Framework.getStoreRegistry().getStore('herobanner');
    const heroBook = herobanner.getHeroBook() || {};
    const { courses = [], course_id: activeCourseId } = heroBook;
    if (!courses.length) return null;
    const row = activeCourseId ? courses.find(course => course.course_id === activeCourseId) : null;

    return (row || courses[0]).course_end_date || null;
  };

  /** Returns course start/end ISO dates for assisted schedule date-picker bounds. */
  static getCourseDateBoundsFromHeroBook = () => ({
    courseStartDate: CommonUtils.getCourseStartDateFromHeroBook(),
    courseEndDate: CommonUtils.getCourseEndDateFromHeroBook()
  });

  /**
   * Check if the user is a revel validated instructor
   * @returns {Boolean} - true/false
   */
  static isRevelValidatedInstructor = () => {
    const user = Framework.getStoreRegistry().getStore('user');
    const userStore = getSnapshot(user);
    const { userRole } = userStore || {};
    const isRevelValidatedInstructor = CommonUtils.localStringCompare(
      userRole, constants.USER_PROFILE_ROLES.INSTRUCTOR_REVEL_VALIDATED
    );

    return isRevelValidatedInstructor;
  };

  /**
   * Converts a date/timestamp value to milliseconds for comparison.
   * Supports UTC ISO strings (e.g. 2026-05-21T06:10:58.626Z), Date objects, and numeric timestamps.
   *
   * @param {Date|number|string|null|undefined} value
   * @returns {number|null}
   */
  static getComparableTimestamp = (value) => {
    if (value == null || value === '') {
      return null;
    }

    if (value instanceof Date) {
      const time = value.getTime();

      return Number.isNaN(time) ? null : time;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value === 'string') {
      const time = Date.parse(value);

      return Number.isNaN(time) ? null : time;
    }

    return null;
  };

  /**
 * Sorts items by a timestamp field in descending order (most recent first).
 * Items with missing or invalid timestamps are placed at the end.
 *
 * @param {Array} items
 * @param {Function} getTimestampValue - Returns raw timestamp from an item
 * @returns {Array}
 */
  static sortByTimestampDesc = (items, getTimestampValue) => (
    [...items].sort((a, b) => {
      const timeA = CommonUtils.getComparableTimestamp(getTimestampValue(a));
      const timeB = CommonUtils.getComparableTimestamp(getTimestampValue(b));

      if (timeA == null && timeB == null) {
        return 0;
      }

      if (timeA == null) {
        return 1;
      }

      if (timeB == null) {
        return -1;
      }

      return timeB - timeA;
    })
  );

  /**
   * Checks if the book is a TA course
   * @param {Object} book
   * @returns {boolean} - true/false
   */
  static isTACourse = (book) => {
    if (!book) {
      return false;
    }

    const { courseUserRole = constants.COURSE_USER_ROLE.STUDENT } = book || {};

    return CommonUtils.localStringCompare(courseUserRole, constants.COURSE_USER_ROLE.TA);
  };

  /**
   * Check if the course book preference is fetched
   * @param {Object} params
   * @param {String} params.course_id
   * @param {Boolean} params.isUserBookPreferenceFetched
   * @returns {Boolean} - true/false
   */
  static isCourseBookPreferenceFetched = ({ course_id = null, isUserBookPreferenceFetched = false } = {}) => (
    !course_id || isUserBookPreferenceFetched
  );

  /**
   * Check if the channel mapping type is exact or exact-lite
   * @param {String} matchType
   * @returns {Boolean} - true/false
   */
  static isExactOrLiteMatch = (matchType) => {
    const isExact = CommonUtils.localStringCompare(matchType, constants.CHANNEL_MAPPING_TYPES.EXACT);
    const isExactLite = CommonUtils.localStringCompare(matchType, constants.CHANNEL_MAPPING_TYPES.EXACTLITE);

    return isExact || isExactLite;
  };

  /**
   * Check if the channel has access to exact or lite match
   * @param {String} status
   * @param {String} matchType
   * @returns {Boolean} - true/false
   */
  static hasAccessToExactOrLiteMatch = (status, matchType) => {
    const isActive = CommonUtils.localStringCompare(status, constants.ACTIVE);

    return isActive && CommonUtils.isExactOrLiteMatch(matchType);
  };

  /**
   * Standalone non-MLM books (no course) use simplified channel rules.
   * MLM integrated launches always follow course-based access checks,
   * regardless of whether courseId is present.
   *
   * @param {String|null} courseId
   * @param {Boolean} isMLM
   * @returns {Boolean}
   */
  static isStandaloneNonMLMBook = (courseId, isMLM) => !courseId && !isMLM;

  /**
   * Get the computed channel mapping type
   * @param {String} status
   * @param {String} matchType
   * @param {String} courseId
   * @param {Boolean} isMlm
   * @returns {String} - The computed channel mapping type
   */
  static getComputedChannelMappingType = (status, matchType, courseId, isMlm) => {
    if (CommonUtils.isStandaloneNonMLMBook(courseId, isMlm)) {
      return matchType;
    }

    if (CommonUtils.hasAccessToExactOrLiteMatch(status, matchType)) {
      return matchType;
    }

    return constants.CHANNEL_MAPPING_TYPES.GENERIC;
  };

  /**
   * Whether Study Prep should render in eReader.
   *
   * @param {Object} channelAccess - Channels access context
   * @param {Boolean} isStudyPrepEnabled - User book preference (isStudyPrepEnabled)
   * @returns {Boolean}
   */
  static showStudyPrepInEReader(channelAccess = {}) {
    const {
      channel_mapping_type: channelMappingType,
      hasAccessToExactOrLiteMatch = false,
      course_id: courseId,
      product_entitlements: productEntitlements = {}
    } = channelAccess;

    const isExactOrLiteMatch = CommonUtils.isExactOrLiteMatch(channelMappingType);
    const isMLM = CommonUtils.isIntegratedBMC(productEntitlements?.business_model_code);

    if (CommonUtils.isStandaloneNonMLMBook(courseId, isMLM)) {
      return isExactOrLiteMatch;
    }

    // Todo: The UserBookPreference API determines the default value of isStudyPrepEnabled using the old Channels API instead of the new Channels Access API. Once this is resolved, we can include isStudyPrepEnabled with the existing conditions to determine whether to show or hide the Channels mini player. (Should be fixed by marin team)
    return isExactOrLiteMatch && hasAccessToExactOrLiteMatch;
  }

  /**
   * Resolves Study Prep feature flags for the active hero book and user.
   *
   * @param {Object} heroBook - Active hero book snapshot
   * @param {Object} userSnapshot - User store snapshot
   * @returns {Object} Study Prep visibility config
   */
  static getAHStudyPrepConfig(heroBook = {}, userSnapshot = {}) {
    const {
      course_id: courseId,
      channel_mapping_type: channelMappingType,
      hasAccessToExactOrLiteMatch = false,
      product_entitlements: productEntitlements = {}
    } = heroBook;

    const hasFullAccess = !!userSnapshot?.hasChannelsAccess;
    const isExactOrLiteMatch = CommonUtils.isExactOrLiteMatch(channelMappingType);
    const isMLM = CommonUtils.isIntegratedBMC(productEntitlements?.business_model_code);

    if (CommonUtils.isStandaloneNonMLMBook(courseId, isMLM)) {
      return {
        showMappedChannelTemplateInAuthHome: isExactOrLiteMatch,
        showPopularChannelSlider: true
      };
    }

    // course + no exact/lite match
    if (!isExactOrLiteMatch) {
      return {
        showMappedChannelTemplateInAuthHome: false,
        showPopularChannelSlider: hasFullAccess
      };
    }

    // course + exact/lite match + full access
    if (hasFullAccess && hasAccessToExactOrLiteMatch) {
      return {
        showMappedChannelTemplateInAuthHome: true,
        showPopularChannelSlider: true
      };
    }

    // course + exact/lite match only
    if (hasAccessToExactOrLiteMatch) {
      return {
        showMappedChannelTemplateInAuthHome: true,
        showPopularChannelSlider: false
      };
    }

    // course + exact/lite match but no access
    return {
      showMappedChannelTemplateInAuthHome: false,
      showPopularChannelSlider: false
    };
  }

  /**
   * Build channelAccess object for eReader Study Prep visibility checks.
   *
   * @param {Object} product - Product model instance
   * @param {String} [courseId] - Optional course id override
   * @returns {Object}
   */
  static getChannelAccess(product = {}, courseId = null) {
    return {
      channel_mapping_type: product.channel_mapping_type,
      hasAccessToExactOrLiteMatch: product.hasAccessToExactOrLiteMatch ?? false,
      course_id: courseId ?? null,
      product_entitlements: { business_model_code: product.getBusinessModelCode() }
    };
  }

  /**
   * Map channels access API response to book fields.
   *
   * @param {Object} params
   * @returns {Object}
   */
  static mapChannelsAccess({
    channel,
    status,
    matchType,
    courseId,
    isMlm
  }) {
    return {
      channel_id: channel?.courseId,
      channel_mapping_type: CommonUtils.getComputedChannelMappingType(status, matchType, courseId, isMlm),
      channel_name: channel?.title,
      hasAccessToExactOrLiteMatch: CommonUtils.hasAccessToExactOrLiteMatch(status, matchType)
    };
  }

  /**
   * Request channels access for a book.
   *
   * @param {String} bookId - Book ID
   * @param {Function} [callback] - Called on success, error, or when bookId is missing
   * @returns {void}
   */
  static requestChannelsAccess(bookId, callback) {
    if (!bookId) {
      if (typeof callback === 'function') {
        callback(null, true);
      }

      return;
    }

    Framework.getEventManager().publish(
      constants.CHANNELS_ACCESS_REQUESTED,
      { bookId, callback }
    );
  }

  /**
   * Stable data attributes for targeting AET elements from in-app guides (Pendo) and analytics.
   * Pass the result as `dataAttributes` to @aet/components, or spread it onto a local element.
   *
   * @param {string} id value from AET_ELEMENT_IDS
   * @param {Object} options
   * @param {string} [options.state] value from ELEMENT_STATES
   * @param {string} [options.location] value from AET_EVENT.LOCATION_IN_APP
   * @returns {Object} e.g. { 'data-componentid': 'AET_ADD_ASSIGNMENT_BUTTON' }
   */
  static getElementDataAttributes(id, { state, location } = {}) {
    const { ID, STATE, LOCATION } = constants.ELEMENT_DATA_ATTRIBUTES;

    return {
      [ID]: id,
      ...(state ? { [STATE]: state } : {}),
      ...(location ? { [LOCATION]: location } : {})
    };
  }
}
